// 좌석 배치 관리
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, onSnapshot, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Student, Teacher, isTeacher } from '../../../utils/firebase';
import { printSeatingArrangement } from '../../../utils/printSeatingArrangement';
import {
  getTeacherSeatCombinedScale,
  getTeacherSeatRenderSizes,
  getTeacherSeatViewportScale
} from '../../../utils/seatBoardLayout';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import DropdownButton from '../../../components/ui/DropdownButton';
import AddNewSeatDialog from '../../../components/ui/AddNewSeatDialog';
import InputField from '../../../components/ui/InputField';
import ToggleSwitch from '../../../components/ui/ToggleSwitch';
import Intersect from '../../../components/icons/Intersect';
import PlusIcon from '../../../components/icons/PlusIcon';
import MinusIcon from '../../../components/icons/MinusIcon';
import PublishButton from '../../../components/icons/PublishButton';
import SettingIcon from '../../../components/icons/SettingIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import CloseIcon from '../../../components/icons/CloseIcon';

type SeatingArrangementStatus = 'draft' | 'published';

interface Seat {
  studentId: string | null;
  studentName: string | null;
  isDisabled: boolean;
  position: {
    row: number;
    col: number;
  };
  bidAmount: number | null;
  bidTime: Date | null;
  /** 자리세(포인트 등). 미설정·레거시 문서는 undefined */
  seatTax?: number | null;
  groupId?: string; // 같은 그룹(모둠/짝꿍)에 속한 좌석 표시
  absolutePosition?: {
    x: number;
    y: number;
  }; // 자유 배치 모드에서 사용하는 절대 좌표
  isCustomPosition?: boolean; // 사용자가 수동으로 배치했는지 여부
  /** 학생 좌석 예약 페이지에서 저장 (studentId 대신 사용) */
  uid?: string | null;
  name?: string | null;
  isReserved?: boolean;
  reservedAt?: Date | null;
}

interface SeatingSettings {
  rows: number;
  cols: number;
  isExamMode: boolean;
  isPairedSeating: boolean;
  boardPosition: 'top' | 'bottom';
  enabled: boolean;
  startDate: Date | null;
  endDate: Date | null;
  reservationStartDate: Date | null;
  reservationEndDate: Date | null;
  reservationType: 'item' | 'auction' | 'firstCome';
  layoutTemplate?: 'individual' | 'pair' | 'group4' | 'group6' | 'u-shape' | 'square' | 'custom';
}

interface SeatingArrangement {
  id: string;
  name: string;
  seats: Record<string, Seat>;
  settings: SeatingSettings;
  status: 'draft' | 'published' | 'reservation' | 'normal';
  createdAt: Date;
  updatedAt: Date;
  reservationEndDate?: Date;
  reservationType?: 'item' | 'point';
}

interface SeatManagementProps {
  schoolId: string;
  classId: string;
}

const SeatManagement: React.FC<SeatManagementProps> = ({ schoolId, classId }) => {
  const seatingPrintRef = useRef<HTMLDivElement | null>(null);
  const seatBoardFitAreaRef = useRef<HTMLDivElement | null>(null);
  const seatBoardContentRef = useRef<HTMLDivElement | null>(null);
  const { currentUser } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [seatingArrangement, setSeatingArrangement] = useState<SeatingArrangement | null>(null);
  const [reservationArrangement, setReservationArrangement] = useState<SeatingArrangement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [isDisableMode, setIsDisableMode] = useState(false);
  const [isSeatTaxMode, setIsSeatTaxMode] = useState(false);
  const [seatTaxTargetKey, setSeatTaxTargetKey] = useState<string | null>(null);
  const [seatTaxDraft, setSeatTaxDraft] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [isReservationMode, setIsReservationMode] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('current');
  // 모든 자리표 목록 (날짜별 선택용)
  const [allArrangements, setAllArrangements] = useState<SeatingArrangement[]>([]);
  const [isEditLayoutMode, setIsEditLayoutMode] = useState(false); // 좌석 위치 편집 모드
  /** 학생 목록에서 드래그할 때 좌석 칩 displacement(transform) 끄기용 */
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [selectedSeatForMove, setSelectedSeatForMove] = useState<string | null>(null); // 이동할 좌석 선택
  
  // 편집 모드 그리드 크기 (동적 조절 가능)
  const [gridRows, setGridRows] = useState(7);
  const [gridCols, setGridCols] = useState(10);

  // 새 자리 배치 다이얼로그 상태
  const [isAddNewSeatDialogOpen, setIsAddNewSeatDialogOpen] = useState(false);
  // 자리 배치 설정 편집 다이얼로그 상태
  const [isEditSeatSettingsDialogOpen, setIsEditSeatSettingsDialogOpen] = useState(false);
  
  // 예약 시작 폼 상태
  const [reservationStartDate, setReservationStartDate] = useState<string>('');
  const [reservationEndDate, setReservationEndDate] = useState<string>('');
  const [reservationRows, setReservationRows] = useState<number>(4);
  const [reservationCols, setReservationCols] = useState<number>(6);
  const [reservationIsPairedSeating, setReservationIsPairedSeating] = useState<boolean>(false);
  const [reservationBoardPosition, setReservationBoardPosition] = useState<'top' | 'bottom'>('top');
  const [reservationType, setReservationType] = useState<'item' | 'point' | 'firstCome'>('item');

  const [teacherViewportScale, setTeacherViewportScale] = useState(() =>
    typeof window !== 'undefined'
      ? getTeacherSeatViewportScale(window.innerWidth, window.innerHeight)
      : 1
  );

  useEffect(() => {
    const update = () => {
      setTeacherViewportScale(
        getTeacherSeatViewportScale(window.innerWidth, window.innerHeight)
      );
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const [boardFitAreaSize, setBoardFitAreaSize] = useState({ w: 0, h: 0 });
  /** 추정 스케일 위에 DOM 실측으로 한 번 더 줄이는 보정 배율 */
  const [domFitFactor, setDomFitFactor] = useState(1);

  useLayoutEffect(() => {
    const el = seatBoardFitAreaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      setBoardFitAreaSize((prev) => (prev.w === 0 && prev.h === 0 ? prev : { w: 0, h: 0 }));
      return;
    }
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setBoardFitAreaSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [
    isReservationMode,
    isEditLayoutMode,
    seatingArrangement?.id,
    reservationArrangement?.id
  ]);

  // 영역·그리드가 바뀌면 보정 배율을 리셋한 뒤 다시 실측한다.
  useLayoutEffect(() => {
    setDomFitFactor(1);
  }, [boardFitAreaSize.w, boardFitAreaSize.h, gridCols, gridRows, isEditLayoutMode, isReservationMode]);

  const teacherCombinedScale = useMemo(() => {
    if (boardFitAreaSize.w < 80 || boardFitAreaSize.h < 80) {
      return teacherViewportScale * domFitFactor;
    }
    return (
      getTeacherSeatCombinedScale({
        availWidth: boardFitAreaSize.w,
        availHeight: boardFitAreaSize.h,
        gridCols,
        gridRows,
        viewportComfortScale: teacherViewportScale,
        fitHeight: true
      }) * domFitFactor
    );
  }, [boardFitAreaSize, gridCols, gridRows, teacherViewportScale, domFitFactor]);

  const teacherSeatSizes = useMemo(
    () => getTeacherSeatRenderSizes(teacherCombinedScale),
    [teacherCombinedScale]
  );

  // 렌더 후 실제 콘텐츠가 영역을 넘치면 보정 배율로 추가 축소 (스크롤 제거)
  useLayoutEffect(() => {
    const area = seatBoardFitAreaRef.current;
    const content = seatBoardContentRef.current;
    if (!area || !content) return;
    if (boardFitAreaSize.w < 80 || boardFitAreaSize.h < 80) return;

    const availW = area.clientWidth;
    const availH = area.clientHeight;
    const contentW = content.scrollWidth;
    const contentH = content.scrollHeight;
    if (contentW < 8 || contentH < 8) return;

    const factor = Math.min(availW / contentW, availH / contentH);
    if (factor >= 0.995) return;

    setDomFitFactor((prev) => {
      const next = Math.max(0.18, prev * factor * 0.96);
      return Math.abs(next - prev) < 0.003 ? prev : next;
    });
  }, [
    teacherSeatSizes,
    boardFitAreaSize,
    gridCols,
    gridRows,
    isEditLayoutMode,
    seatingArrangement?.id,
    reservationArrangement?.id
  ]);

  // 그리드 크기 변경 시 범위를 벗어난 좌석 제거
  useEffect(() => {
    if (!isEditLayoutMode || !currentUser || !isTeacher(currentUser)) {
      return;
    }

    const arrangement = isReservationMode ? reservationArrangement : seatingArrangement;
    if (!arrangement) {
      return;
    }

    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) {
      return;
    }

    const seats = { ...arrangement.seats };
    let hasChanges = false;

    // 범위를 벗어난 좌석 찾기 및 제거
    Object.keys(seats).forEach(seatKey => {
      const [row, col] = seatKey.split('-').map(Number);
      
      // 그리드 범위를 벗어난 좌석 제거
      if (row >= gridRows || col >= gridCols) {
        delete seats[seatKey];
        hasChanges = true;
      }
    });

    // 변경사항이 있으면 Firestore 업데이트
    if (hasChanges) {
      const updateSeats = async () => {
        try {
          const docRef = isReservationMode ? 'reservation' : selectedPeriod;
          const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
          await updateDoc(arrangementRef, {
            seats,
            updatedAt: new Date()
          });

          if (isReservationMode) {
            setReservationArrangement(prev => prev ? { ...prev, seats } : null);
          } else {
            setSeatingArrangement(prev => prev ? { ...prev, seats } : null);
          }
        } catch (err) {
          console.error('Error removing out-of-range seats:', err);
        }
      };

      updateSeats();
    }
  }, [gridRows, gridCols, isEditLayoutMode, isReservationMode, reservationArrangement, seatingArrangement, currentUser, selectedPeriod]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!currentUser?.classId || !currentUser?.schoolId) {
          setError('필수 정보가 누락되었습니다. 다시 로그인해주세요.');
          return;
        }

        // 학생 목록 가져오기
        const studentsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students`);
        const studentsSnapshot = await getDocs(studentsRef);
        const studentsData = studentsSnapshot.docs.map(doc => {
          const data = doc.data() as Record<string, unknown>;
          const rawNum = data.number;
          const num =
            rawNum === null || rawNum === undefined || rawNum === ''
              ? 0
              : typeof rawNum === 'number'
                ? rawNum
                : Number(rawNum);
          return {
            uid: doc.id,
            ...data,
            number: Number.isFinite(num) ? num : 0
          } as Student;
        });
        setStudents(studentsData);

        // 좌석 배치 정보 가져오기
        const arrangementRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/seatingArrangements`, 'current');
        const arrangementDoc = await getDoc(arrangementRef);
        
        if (arrangementDoc.exists()) {
          const data = arrangementDoc.data();
          const seatingArrangementData: SeatingArrangement = {
            id: arrangementDoc.id,
            name: '현재 좌석배치도',
            seats: data.seats || {},
            settings: {
              rows: data.settings?.rows || 5,
              cols: data.settings?.cols || 6,
              isExamMode: data.settings?.isExamMode || false,
              isPairedSeating: data.settings?.isPairedSeating || false,
              boardPosition: data.settings?.boardPosition || 'top',
              enabled: data.settings?.enabled || false,
              startDate: data.settings?.startDate?.toDate() || null,
              endDate: data.settings?.endDate?.toDate() || null,
              reservationStartDate: data.settings?.reservationStartDate?.toDate() || null,
              reservationEndDate: data.settings?.reservationEndDate?.toDate() || null,
              reservationType: data.settings?.reservationType || 'item',
            },
            status: data.status || 'draft',
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          };
          setSeatingArrangement(seatingArrangementData);
          // 그리드 크기를 settings에서 가져온 값으로 초기화
          setGridRows(seatingArrangementData.settings.rows);
          setGridCols(seatingArrangementData.settings.cols);
        } else {
          // 초기 좌석 생성 (4행 6열)
          const initialSeats: Record<string, Seat> = {};
          for (let row = 0; row < 4; row++) {
            for (let col = 0; col < 6; col++) {
              const seatKey = `${row}-${col}`;
              initialSeats[seatKey] = {
                studentId: null,
                studentName: null,
                isDisabled: false,
                position: { row, col },
                bidAmount: null,
                bidTime: null,
                seatTax: null
              };
            }
          }

          const newArrangementData: SeatingArrangement = {
            id: 'current',
            name: '현재 좌석배치도',
            seats: initialSeats,
            settings: {
              rows: 4,
              cols: 6,
              isExamMode: false,
              isPairedSeating: false,
              boardPosition: 'top',
              enabled: false,
              startDate: null,
              endDate: null,
              reservationStartDate: null,
              reservationEndDate: null,
              reservationType: 'item',
            },
            status: 'draft',
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          // Firestore에 저장
          const arrangementRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/seatingArrangements`, 'current');
          await setDoc(arrangementRef, {
            ...newArrangementData,
            createdAt: new Date(),
            updatedAt: new Date()
          });
          
          setSeatingArrangement(newArrangementData);
        }
      } catch (err) {
        console.error('Error fetching seating arrangement:', err);
        setError('좌석 배치 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // 예약 모드 좌석배치도 모니터링
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) return;
    
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) return;

    const reservationRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, 'reservation');
    const unsubscribe = onSnapshot(reservationRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setReservationArrangement({
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          reservationEndDate: data.reservationEndDate?.toDate(),
          settings: {
            ...data.settings,
            startDate: data.settings?.startDate?.toDate() || null,
            endDate: data.settings?.endDate?.toDate() || null,
            reservationStartDate: data.settings?.reservationStartDate?.toDate() || null,
            reservationEndDate: data.settings?.reservationEndDate?.toDate() || null
          }
        } as SeatingArrangement);
      } else {
        setReservationArrangement(null);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  /** 예약 모드일 때 편집 그리드 행·열은 항상 예약 자리표 `settings`와 동기화 (현재 자리표 그리드 상태가 남지 않도록) */
  useEffect(() => {
    if (!isReservationMode || !reservationArrangement?.settings) return;
    const { rows, cols } = reservationArrangement.settings;
    if (typeof rows === 'number' && typeof cols === 'number' && rows > 0 && cols > 0) {
      setGridRows(rows);
      setGridCols(cols);
    }
  }, [
    isReservationMode,
    reservationArrangement?.settings?.rows,
    reservationArrangement?.settings?.cols
  ]);

  /** 현재 자리 관리 모드일 때 그리드는 `current` 자리표 settings와 동기화 */
  useEffect(() => {
    if (isReservationMode || !seatingArrangement?.settings) return;
    const { rows, cols } = seatingArrangement.settings;
    if (typeof rows === 'number' && typeof cols === 'number' && rows > 0 && cols > 0) {
      setGridRows(rows);
      setGridCols(cols);
    }
  }, [
    isReservationMode,
    seatingArrangement?.settings?.rows,
    seatingArrangement?.settings?.cols
  ]);

  // 모든 자리표 가져오기 (날짜별 선택용)
  useEffect(() => {
    const fetchAllArrangements = async () => {
      try {
        if (!currentUser?.classId || !currentUser?.schoolId) {
          return;
        }

        const arrangementsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/seatingArrangements`);
        const arrangementsSnapshot = await getDocs(arrangementsRef);
        
        const arrangements: SeatingArrangement[] = [];
        arrangementsSnapshot.forEach((doc) => {
          const data = doc.data();
          // 'current'와 'reservation'은 제외 (이미 별도로 관리)
          if (doc.id !== 'current' && doc.id !== 'reservation') {
            arrangements.push({
              id: doc.id,
              name: data.name || '자리표',
              seats: data.seats || {},
              settings: {
                rows: data.settings?.rows || 5,
                cols: data.settings?.cols || 6,
                isExamMode: data.settings?.isExamMode || false,
                isPairedSeating: data.settings?.isPairedSeating || false,
                boardPosition: data.settings?.boardPosition || 'top',
                enabled: data.settings?.enabled || false,
                startDate: data.settings?.startDate?.toDate() || null,
                endDate: data.settings?.endDate?.toDate() || null,
                reservationStartDate: data.settings?.reservationStartDate?.toDate() || null,
                reservationEndDate: data.settings?.reservationEndDate?.toDate() || null,
                reservationType: data.settings?.reservationType || 'item',
              },
              status: data.status || 'draft',
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date()
            });
          }
        });

        // 시작일 기준으로 정렬 (최신 순)
        arrangements.sort((a, b) => {
          const aDate = a.settings.startDate?.getTime() || 0;
          const bDate = b.settings.startDate?.getTime() || 0;
          return bDate - aDate;
        });

        setAllArrangements(arrangements);
      } catch (err) {
        console.error('Error fetching all arrangements:', err);
      }
    };

    fetchAllArrangements();
  }, [currentUser?.classId, currentUser?.schoolId]);

  // 선택한 기간(자리표)에 따라 자리표 로드
  useEffect(() => {
    const loadSelectedArrangement = async () => {
      if (selectedPeriod === 'current' || !currentUser?.classId || !currentUser?.schoolId) {
        return; // 'current'는 이미 fetchData에서 로드됨
      }

      try {
        const arrangementRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/seatingArrangements`, selectedPeriod);
        const arrangementDoc = await getDoc(arrangementRef);
        
        if (arrangementDoc.exists()) {
          const data = arrangementDoc.data();
          const seatingArrangementData: SeatingArrangement = {
            id: arrangementDoc.id,
            name: data.name || '자리표',
            seats: data.seats || {},
            settings: {
              rows: data.settings?.rows || 5,
              cols: data.settings?.cols || 6,
              isExamMode: data.settings?.isExamMode || false,
              isPairedSeating: data.settings?.isPairedSeating || false,
              boardPosition: data.settings?.boardPosition || 'top',
              enabled: data.settings?.enabled || false,
              startDate: data.settings?.startDate?.toDate() || null,
              endDate: data.settings?.endDate?.toDate() || null,
              reservationStartDate: data.settings?.reservationStartDate?.toDate() || null,
              reservationEndDate: data.settings?.reservationEndDate?.toDate() || null,
              reservationType: data.settings?.reservationType || 'item',
            },
            status: data.status || 'draft',
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          };
          setSeatingArrangement(seatingArrangementData);
          // 그리드 크기도 업데이트
          setGridRows(seatingArrangementData.settings.rows);
          setGridCols(seatingArrangementData.settings.cols);
        }
      } catch (err) {
        console.error('Error loading selected arrangement:', err);
        setError('선택한 자리표를 불러오는 중 오류가 발생했습니다.');
      }
    };

    loadSelectedArrangement();
  }, [selectedPeriod, currentUser?.classId, currentUser?.schoolId]);

  // 교사 권한이 없는 경우
  if (!currentUser || !isTeacher(currentUser)) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#ffebee', 
          color: '#c62828', 
          borderRadius: '8px' 
        }}>
          교사 권한이 필요합니다.
        </div>
      </div>
    );
  }

  const currentArrangement = isReservationMode ? reservationArrangement : seatingArrangement;

  // 드래그 앤 드롭 처리
  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !currentArrangement || !currentUser || !isTeacher(currentUser)) {
      return;
    }

    const { source, destination } = result;
    
    // 같은 위치로 드래그한 경우 무시
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const getOccupantPayload = (seat: Seat) => ({
      studentId: seat.studentId ?? null,
      studentName: seat.studentName ?? null,
      uid: seat.uid ?? null,
      name: seat.name ?? null,
      isReserved: seat.isReserved ?? false,
      reservedAt: seat.reservedAt ?? null
    });
    const clearOccupantPayload = {
      studentId: null,
      studentName: null,
      uid: null,
      name: null,
      isReserved: false,
      reservedAt: null
    } as const;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      // 편집 모드에서 좌석 위치 이동 (그리드 간 드래그)
      if (isEditLayoutMode && source.droppableId.startsWith('grid-cell-')) {
        const sourceSeatKey = result.draggableId.replace('seat-', '');
        const [destRow, destCol] = destination.droppableId.replace('grid-cell-', '').split('-').map(Number);
        
        await handleSeatPositionChange(sourceSeatKey, destRow, destCol);
        return;
      }

      // 학생 목록에서 좌석으로 드래그한 경우
      if (source.droppableId === 'studentList') {
        const studentId = result.draggableId.replace('student-list-', '');
        const draggedStudent = students.find(student => student.uid === studentId);
        if (!draggedStudent) return;
        
        const seatKey = destination.droppableId;
        const destinationSeat = currentArrangement.seats[seatKey];
        
        if (!destinationSeat || destinationSeat.isDisabled) {
          toast.error('비활성화된 좌석으로 이동할 수 없습니다.');
          return;
        }

        const updatedSeats = { ...currentArrangement.seats };
        
        // 이전 좌석에서 학생 제거
        Object.entries(updatedSeats).forEach(([key, seat]) => {
          if (getSeatOccupantId(seat) === draggedStudent.uid) {
            updatedSeats[key] = {
              ...seat,
              ...clearOccupantPayload
            };
          }
        });

        // 새 좌석에 학생 배정
        updatedSeats[seatKey] = {
          ...updatedSeats[seatKey],
          ...clearOccupantPayload,
          studentId: draggedStudent.uid,
          studentName: draggedStudent.name
        };

        // Firestore 업데이트
        const docRef = isReservationMode ? 'reservation' : 'current';
        const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
        await updateDoc(arrangementRef, {
          seats: updatedSeats,
          updatedAt: new Date()
        });

        if (isReservationMode) {
          setReservationArrangement(prev => prev ? { ...prev, seats: updatedSeats } : null);
        } else {
          setSeatingArrangement(prev => prev ? { ...prev, seats: updatedSeats } : null);
        }
        
        toast.success(`${draggedStudent.name} 학생이 좌석에 배정되었습니다.`);
        return;
      }

      // 좌석 → 학생 목록으로 드래그: 해당 좌석 배정 취소
      if (destination.droppableId === 'studentList' && source.droppableId !== 'studentList') {
        let sourceSeatKey: string;
        if (source.droppableId.startsWith('grid-cell-')) {
          sourceSeatKey = result.draggableId.replace(/^seat-/, '');
        } else {
          sourceSeatKey = source.droppableId;
        }
        const seatToClear = currentArrangement.seats[sourceSeatKey];
        if (!seatToClear || seatToClear.isDisabled || !seatToClear.studentId) {
          return;
        }

        const updatedSeats = { ...currentArrangement.seats };
        updatedSeats[sourceSeatKey] = {
          ...updatedSeats[sourceSeatKey],
          ...clearOccupantPayload
        };

        const docRef = isReservationMode ? 'reservation' : 'current';
        const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
        await updateDoc(arrangementRef, {
          seats: updatedSeats,
          updatedAt: new Date()
        });

        if (isReservationMode) {
          setReservationArrangement(prev => (prev ? { ...prev, seats: updatedSeats } : null));
        } else {
          setSeatingArrangement(prev => (prev ? { ...prev, seats: updatedSeats } : null));
        }

        toast.success('좌석 배정을 취소했습니다.');
        return;
      }

      // 좌석 간 드래그인 경우 (학생 교환)
      const sourceSeatKey = source.droppableId;
      const destinationSeatKey = destination.droppableId;
      
      const updatedSeats = { ...currentArrangement.seats };
      const sourceSeat = updatedSeats[sourceSeatKey];
      const destinationSeat = updatedSeats[destinationSeatKey];
      
      if (!sourceSeat || !destinationSeat || destinationSeat.isDisabled) {
        toast.error('유효하지 않은 좌석입니다.');
        return;
      }

      // 학생 정보(교사 배정 + 학생 예약 필드 포함) 교환
      const sourceOccupant = getOccupantPayload(sourceSeat);
      const destinationOccupant = getOccupantPayload(destinationSeat);
      
      updatedSeats[sourceSeatKey] = {
        ...sourceSeat,
        ...destinationOccupant
      };
      
      updatedSeats[destinationSeatKey] = {
        ...destinationSeat,
        ...sourceOccupant
      };

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: updatedSeats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats: updatedSeats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats: updatedSeats } : null);
      }
      
      toast.success('좌석이 교환되었습니다.');
    } catch (err) {
      console.error('Error in drag and drop:', err);
      setError('좌석 이동 중 오류가 발생했습니다.');
    }
  };

  // 자리 섞기 함수
  const handleShuffle = async () => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const seats = { ...currentArrangement.seats };
      
      // 배정된 학생이 있는 좌석만 필터링
      const assignedSeats = Object.entries(seats).filter(([_, seat]) => seat.studentId !== null);
      
      // 학생 정보만 추출
      const studentsList = assignedSeats.map(([_, seat]) => ({
        studentId: seat.studentId,
        studentName: seat.studentName
      }));
      
      // 학생 순서 섞기
      for (let i = studentsList.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [studentsList[i], studentsList[j]] = [studentsList[j], studentsList[i]];
      }
      
      // 섞은 학생들을 원래 좌석에 다시 배정
      const updatedSeats = { ...seats };
      assignedSeats.forEach(([seatKey, _], index) => {
        updatedSeats[seatKey] = {
          ...updatedSeats[seatKey],
          studentId: studentsList[index].studentId,
          studentName: studentsList[index].studentName
        };
      });

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: updatedSeats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats: updatedSeats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats: updatedSeats } : null);
      }
      
      toast.success('자리가 섞였습니다.');
    } catch (err) {
      console.error('Error shuffling seats:', err);
      setError('자리를 섞는 중 오류가 발생했습니다.');
    }
  };

  // 랜덤 배치 함수
  const handleRandomAssign = async () => {
    console.log('랜덤 배치 버튼 클릭됨 (SeatManagement.tsx)');
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) {
      console.log('조건 불만족으로 함수 종료');
      return;
    }
    
    try {
      console.log('=== 랜덤 배치 시작 ===');
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      // 모든 좌석 초기화 (비활성화된 좌석 제외)
      const seats: Record<string, Seat> = {};
      Object.keys(currentArrangement.seats).forEach(key => {
        const originalSeat = currentArrangement.seats[key];
        seats[key] = {
          ...originalSeat,
          // 비활성화된 좌석은 그대로 유지, 활성화된 좌석은 학생 정보 초기화
          studentId: originalSeat.isDisabled ? originalSeat.studentId : null,
          studentName: originalSeat.isDisabled ? originalSeat.studentName : null
        };
      });
      
      // 활성화된 좌석 키만 추출
      const availableSeatKeys: string[] = [];
      Object.keys(seats).forEach(key => {
        if (!seats[key].isDisabled) {
          availableSeatKeys.push(key);
        }
      });
      
      console.log('활성화된 전체 자리 수:', availableSeatKeys.length);
      console.log('셔플 전 자리 순서:', [...availableSeatKeys]);
      
      // 활성화된 좌석 키를 Fisher-Yates 알고리즘으로 랜덤하게 섞기
      const shuffledSeatKeys: string[] = [...availableSeatKeys];
      for (let i = shuffledSeatKeys.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledSeatKeys[i], shuffledSeatKeys[j]] = [shuffledSeatKeys[j], shuffledSeatKeys[i]];
      }
      
      console.log('셔플 후 자리 순서:', shuffledSeatKeys);
      
      // 학생 순서 섞기
      const shuffledStudents = [...students];
      for (let i = shuffledStudents.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledStudents[i], shuffledStudents[j]] = [shuffledStudents[j], shuffledStudents[i]];
      }
      
      console.log('셔플 후 학생 순서:', shuffledStudents.map(s => ({ name: s.name, uid: s.uid })));
      
      // 배정될 자리와 학생 정보 저장
      const assignments: Array<{ seat: string; student: string; studentName: string }> = [];
      
      // 랜덤하게 섞인 좌석에 학생 배정
      shuffledStudents.forEach((student, index) => {
        if (index < shuffledSeatKeys.length) {
          const seatKey = shuffledSeatKeys[index];
          seats[seatKey] = {
            ...seats[seatKey],
            studentId: student.uid,
            studentName: student.name
          };
          assignments.push({
            seat: seatKey,
            student: student.uid,
            studentName: student.name
          });
        }
      });
      
      console.log('=== 배정 결과 ===');
      console.log('배정된 자리와 학생:', assignments);
      assignments.forEach(({ seat, studentName }) => {
        console.log(`${seat} -> ${studentName}`);
      });
      console.log('================');

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: seats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats: seats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats: seats } : null);
      }

      toast.success('학생들이 랜덤으로 배치되었습니다.');
    } catch (err) {
      console.error('Error randomly assigning students:', err);
      setError('학생을 랜덤으로 배치하는 중 오류가 발생했습니다.');
    }
  };

  // 번호 순 배치 함수
  const handleAssignByNumber = async () => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const seats = { ...currentArrangement.seats };
      
      // 학생 목록을 번호 순으로 정렬
      const sortedStudentsList = [...students].sort((a, b) => {
        const numA = a.number || 0;
        const numB = b.number || 0;
        return numA - numB;
      });

      // 모든 좌석 초기화
      Object.keys(seats).forEach(key => {
        seats[key] = {
          ...seats[key],
          studentId: null,
          studentName: null
        };
      });

      // 번호 순서대로 좌석 배정 (1열부터 채우는 방식)
      let studentIndex = 0;
      for (let col = 0; col < (currentArrangement.settings.cols || 6); col++) {
        for (let row = 0; row < (currentArrangement.settings.rows || 4); row++) {
          const seatKey = `${row}-${col}`;
          
          // 해당 좌석이 존재하고 비활성화되지 않은 경우에만 배정
          if (seats[seatKey] && !seats[seatKey].isDisabled && studentIndex < sortedStudentsList.length) {
            const student = sortedStudentsList[studentIndex];
            seats[seatKey] = {
              ...seats[seatKey],
              studentId: student.uid,
              studentName: student.name
            };
            studentIndex++;
          }
        }
      }

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats } : null);
      }
      
      toast.success('학생들이 번호 순서대로 배치되었습니다.');
    } catch (err) {
      console.error('Error assigning seats by number:', err);
      setError('학생을 번호 순서대로 배치하는 중 오류가 발생했습니다.');
    }
  };

  // 자리 초기화 함수
  const handleResetSeats = async () => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const seats = { ...currentArrangement.seats };
      
      // 모든 좌석에서 학생 제거 (비활성화 상태는 유지)
      Object.keys(seats).forEach(key => {
        seats[key] = {
          ...seats[key],
          studentId: null,
          studentName: null
        };
      });

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats } : null);
      }
      
      toast.success('자리가 초기화되었습니다.');
    } catch (err) {
      console.error('Error resetting seats:', err);
      setError('자리를 초기화하는 중 오류가 발생했습니다.');
    }
  };

  // 미배정 학생 랜덤 배정 함수
  const handleAssignUnassignedStudents = async () => {
    if (!currentArrangement || !currentUser?.schoolId || !currentUser?.classId) return;

    try {
      // 현재 배정된 학생들의 ID 목록
      const assignedStudentIds = Object.values(currentArrangement.seats)
        .filter(seat => seat.studentId !== null)
        .map(seat => seat.studentId);

      // 배정되지 않은 학생들 필터링
      const unassignedStudents = students.filter(student => !assignedStudentIds.includes(student.uid));

      // 빈 자리 찾기
      const emptySeats = Object.entries(currentArrangement.seats)
        .filter(([_, seat]) => seat.studentId === null && !seat.isDisabled)
        .map(([seatKey, seat]) => ({ seatKey, ...seat }));

      if (emptySeats.length === 0) {
        toast.error('빈 자리가 없습니다.');
        return;
      }

      if (unassignedStudents.length === 0) {
        toast.error('배정되지 않은 학생이 없습니다.');
        return;
      }

      // 학생들을 랜덤하게 섞기
      const shuffledStudents = [...unassignedStudents].sort(() => Math.random() - 0.5);

      // 새로운 좌석 배치 생성
      const newSeats = { ...currentArrangement.seats };
      
      // 최소한의 자리 수만큼만 배정
      const assignCount = Math.min(emptySeats.length, shuffledStudents.length);
      
      for (let i = 0; i < assignCount; i++) {
        const seat = emptySeats[i];
        const student = shuffledStudents[i];
        newSeats[seat.seatKey] = {
          ...seat,
          studentId: student.uid,
          studentName: student.name
        };
      }

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: newSeats,
        updatedAt: new Date()
      });

      // 상태 업데이트
      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats: newSeats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats: newSeats } : null);
      }

      toast.success('미배정 학생들이 랜덤하게 배정되었습니다.');
    } catch (error) {
      console.error('Error assigning unassigned students:', error);
      toast.error('학생 배정 중 오류가 발생했습니다.');
    }
  };

  // 비활성화 자리 토글
  const handleToggleSeatDisabled = async (row: number, col: number) => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const seatKey = `${row}-${col}`;
      const updatedSeats = { ...currentArrangement.seats };
      
      if (!updatedSeats[seatKey]) {
        setError('존재하지 않는 좌석입니다.');
        return;
      }

      const isCurrentlyDisabled = updatedSeats[seatKey].isDisabled;
      updatedSeats[seatKey] = {
        ...updatedSeats[seatKey],
        isDisabled: !isCurrentlyDisabled,
        // 비활성화 시 학생 정보 초기화
        studentId: !isCurrentlyDisabled ? null : updatedSeats[seatKey].studentId,
        studentName: !isCurrentlyDisabled ? null : updatedSeats[seatKey].studentName
      };

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: updatedSeats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats: updatedSeats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats: updatedSeats } : null);
      }
      
      toast.success(isCurrentlyDisabled ? '좌석이 활성화되었습니다.' : '좌석이 비활성화되었습니다.');
    } catch (err) {
      console.error('Error toggling seat disabled:', err);
      setError('좌석 비활성화 중 오류가 발생했습니다.');
    }
  };

  const openSeatTaxDialog = (seatKey: string) => {
    if (!currentArrangement?.seats[seatKey]) return;
    const s = currentArrangement.seats[seatKey];
    setSeatTaxTargetKey(seatKey);
    setSeatTaxDraft(s.seatTax != null ? String(s.seatTax) : '');
  };

  const closeSeatTaxDialog = () => {
    setSeatTaxTargetKey(null);
    setSeatTaxDraft('');
  };

  const handleSaveSeatTax = async () => {
    if (!seatTaxTargetKey || !currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    const trimmed = seatTaxDraft.trim();
    let value: number | null;
    if (trimmed === '') {
      value = null;
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        toast.error('0 이상의 정수를 입력해 주세요.');
        return;
      }
      value = n;
    }

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const updatedSeats = { ...currentArrangement.seats };
      const seat = updatedSeats[seatTaxTargetKey];
      if (!seat) {
        toast.error('좌석을 찾을 수 없습니다.');
        return;
      }
      updatedSeats[seatTaxTargetKey] = { ...seat, seatTax: value };

      const docRef = isReservationMode ? 'reservation' : selectedPeriod;
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: updatedSeats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => (prev ? { ...prev, seats: updatedSeats } : null));
      } else {
        setSeatingArrangement(prev => (prev ? { ...prev, seats: updatedSeats } : null));
      }

      closeSeatTaxDialog();
      toast.success(value == null ? '자리세를 해제했습니다.' : '자리세를 저장했습니다.');
    } catch (err) {
      console.error('Error saving seat tax:', err);
      toast.error('자리세 저장 중 오류가 발생했습니다.');
    }
  };

  // 자리 배치표 출력
  const handlePrintSeatingArrangement = async () => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    const boardIconElement =
      seatingPrintRef.current?.querySelector<HTMLElement>('.seat-management-board-icon') ?? null;

    const arrangementToPrint: any = {
      ...currentArrangement,
      settings: {
        ...currentArrangement.settings,
        // 출력 관람자 기준: 칠판은 하단에 보이도록 재구성
        boardPosition: 'bottom' as const
      }
    };

    await printSeatingArrangement({
      seatingArrangement: arrangementToPrint,
      students,
      teacher: currentUser,
      boardIconElement
    });
  };

  // 좌석 위치 수동 변경 (편집 모드)
  const handleSeatPositionChange = async (seatKey: string, newRow: number, newCol: number) => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const oldSeat = currentArrangement.seats[seatKey];
      if (!oldSeat) return;

      const newSeats = { ...currentArrangement.seats };
      const newSeatKey = `${newRow}-${newCol}`;

      // 같은 위치면 무시
      if (seatKey === newSeatKey) return;

      // 목표 위치에 이미 좌석이 있으면 교환
      if (newSeats[newSeatKey]) {
        const targetSeat = newSeats[newSeatKey];
        const [oldRow, oldCol] = seatKey.split('-').map(Number);
        
        // 두 좌석 교환
        newSeats[newSeatKey] = {
          ...oldSeat,
          position: { row: newRow, col: newCol },
          isCustomPosition: true
        };
        
        newSeats[seatKey] = {
          ...targetSeat,
          position: { row: oldRow, col: oldCol },
          isCustomPosition: true
        };
        
        toast.success('두 좌석이 교환되었습니다.');
      } else {
        // 빈 위치로 이동
        delete newSeats[seatKey];
        
        newSeats[newSeatKey] = {
          ...oldSeat,
          position: { row: newRow, col: newCol },
          isCustomPosition: true
        };
        
        toast.success('좌석 위치가 변경되었습니다.');
      }

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: newSeats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats: newSeats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats: newSeats } : null);
      }
    } catch (err) {
      console.error('Error changing seat position:', err);
      setError('좌석 위치 변경 중 오류가 발생했습니다.');
    }
  };

  // 좌석 삭제 (편집 모드)
  const handleDeleteSeat = async (seatKey: string) => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const newSeats = { ...currentArrangement.seats };
      delete newSeats[seatKey];

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : selectedPeriod;
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: newSeats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats: newSeats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats: newSeats } : null);
      }

      toast.success('좌석이 삭제되었습니다.');
    } catch (err) {
      console.error('Error deleting seat:', err);
      setError('좌석 삭제 중 오류가 발생했습니다.');
    }
  };

  // 새 좌석 추가 (편집 모드)
  const handleAddSeat = async (row: number, col: number) => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const seatKey = `${row}-${col}`;
      
      // 이미 좌석이 있는 경우
      if (currentArrangement.seats[seatKey]) {
        toast.error('이미 좌석이 있는 위치입니다.');
        return;
      }

      const newSeats = { ...currentArrangement.seats };
      newSeats[seatKey] = {
        studentId: null,
        studentName: null,
        isDisabled: false,
        position: { row, col },
        bidAmount: null,
        bidTime: null,
        seatTax: null,
        isCustomPosition: true
      };

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : selectedPeriod;
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: newSeats,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { ...prev, seats: newSeats } : null);
      } else {
        setSeatingArrangement(prev => prev ? { ...prev, seats: newSeats } : null);
      }

      toast.success('새 좌석이 추가되었습니다.');
    } catch (err) {
      console.error('Error adding seat:', err);
      setError('좌석 추가 중 오류가 발생했습니다.');
    }
  };

  // 예약 자리 관리 시작
  const handleStartReservation = async (data: {
    startDate: string;
    endDate: string;
    rows: number;
    cols: number;
    isPairedSeating: boolean;
    boardPosition: 'top' | 'bottom';
    reservationType: 'item' | 'point' | 'firstCome';
  }) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) {
      setError('필수 정보가 누락되었습니다.');
      return;
    }

    const { rows, cols, isPairedSeating, boardPosition, startDate, endDate, reservationType } = data;

    // 좌석 생성
    const seats: Record<string, Seat> = {};
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const seatId = `${row}-${col}`;
        
        // 짝 배치인 경우 그룹 ID 설정
        let groupId: string | undefined;
        if (isPairedSeating) {
          groupId = `pair-${row}-${Math.floor(col / 2)}`;
        }
        
        seats[seatId] = {
          studentId: null,
          studentName: null,
          isDisabled: false,
          position: { row, col },
          bidAmount: null,
          bidTime: null,
          seatTax: null,
          ...(groupId !== undefined && { groupId })
        };
      }
    }

    // 날짜 문자열을 Date 객체로 변환
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj = endDate ? new Date(endDate) : null;

    const newArrangement: SeatingArrangement = {
      id: 'reservation',
      name: '예약 좌석배치도',
      seats,
      settings: {
        rows,
        cols,
        isExamMode: false,
        isPairedSeating,
        boardPosition,
        enabled: true,
        startDate: startDateObj,
        endDate: endDateObj,
        reservationStartDate: startDateObj,
        reservationEndDate: endDateObj,
        reservationType: reservationType === 'item' ? 'item' : (reservationType === 'point' ? 'auction' : 'firstCome')
      },
      status: 'reservation',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, 'reservation');
      
      // Firestore에 저장할 데이터 (Date를 Timestamp로 변환)
      const firestoreData = {
        ...newArrangement,
        seats,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        settings: {
          ...newArrangement.settings,
          startDate: startDateObj ? Timestamp.fromDate(startDateObj) : null,
          endDate: endDateObj ? Timestamp.fromDate(endDateObj) : null,
          reservationStartDate: startDateObj ? Timestamp.fromDate(startDateObj) : null,
          reservationEndDate: endDateObj ? Timestamp.fromDate(endDateObj) : null
        }
      };
      
      await setDoc(arrangementRef, firestoreData);
      
      setReservationArrangement(newArrangement);
      setGridRows(rows);
      setGridCols(cols);
      setIsReservationMode(true);
      toast.success('예약 자리 관리가 시작되었습니다.');
      
      // 폼 초기화
      setReservationStartDate('');
      setReservationEndDate('');
      setReservationRows(4);
      setReservationCols(6);
      setReservationIsPairedSeating(false);
      setReservationBoardPosition('top');
      setReservationType('item');
    } catch (error) {
      console.error('예약 배치 생성 중 오류 발생:', error);
      setError('예약 배치 생성 중 오류가 발생했습니다.');
    }
  };

  // 새 자리 배치 생성 (다이얼로그에서 호출)
  const handleCreateNewArrangement = async (data: {
    startDate: string;
    endDate: string;
    rows: number;
    cols: number;
    isPairedSeating: boolean;
    boardPosition: 'top' | 'bottom';
  }) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) {
      setError('필수 정보가 누락되었습니다.');
      return;
    }

    const { rows, cols, isPairedSeating, boardPosition, startDate, endDate } = data;

    // 좌석 생성
    const seats: Record<string, Seat> = {};
    let groupIdCounter = 0;
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const seatId = `${row}-${col}`;
        
        // 짝 배치인 경우 그룹 ID 설정
        let groupId: string | undefined;
        if (isPairedSeating) {
          groupId = `pair-${row}-${Math.floor(col / 2)}`;
        }
        
        seats[seatId] = {
          studentId: null,
          studentName: null,
          isDisabled: false,
          position: { row, col },
          bidAmount: null,
          bidTime: null,
          seatTax: null,
          ...(groupId !== undefined && { groupId })
        };
      }
    }

    // 날짜 문자열을 Date 객체로 변환
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj = endDate ? new Date(endDate) : null;

    const newArrangement: SeatingArrangement = {
      id: isReservationMode ? 'reservation' : 'current',
      name: isReservationMode ? '예약 좌석배치도' : '현재 좌석배치도',
      seats,
      settings: {
        rows,
        cols,
        isExamMode: false,
        isPairedSeating,
        boardPosition,
        enabled: true,
        startDate: startDateObj,
        endDate: endDateObj,
        reservationStartDate: null,
        reservationEndDate: null,
        reservationType: 'item'
      },
      status: 'draft',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      
      // Firestore에 저장할 데이터 (Date를 Timestamp로 변환)
      const firestoreData = {
        ...newArrangement,
        seats,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        settings: {
          ...newArrangement.settings,
          startDate: startDateObj ? Timestamp.fromDate(startDateObj) : null,
          endDate: endDateObj ? Timestamp.fromDate(endDateObj) : null,
          reservationStartDate: null,
          reservationEndDate: null
        }
      };
      
      await setDoc(arrangementRef, firestoreData);
      
      if (isReservationMode) {
        setReservationArrangement(newArrangement);
      } else {
        setSeatingArrangement(newArrangement);
      }
      
      toast.success('새 자리 배치가 생성되었습니다.');
      setIsAddNewSeatDialogOpen(false);
    } catch (error) {
      console.error('배치 생성 중 오류 발생:', error);
      setError('배치 생성 중 오류가 발생했습니다.');
    }
  };

  // 좌석 배치 템플릿 적용
  const applyLayoutTemplate = async (template: 'individual' | 'pair' | 'group4' | 'group6' | 'u-shape' | 'square') => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const rows = currentArrangement.settings.rows;
      const cols = currentArrangement.settings.cols;
      const newSeats: Record<string, Seat> = {};

      // 기존 학생 배정 정보 보존 (학생 관리의 최신 이름 반영)
      const studentAssignments: { studentId: string; studentName: string | null }[] = [];
      Object.values(currentArrangement.seats).forEach(seat => {
        if (seat.studentId) {
          studentAssignments.push({
            studentId: seat.studentId,
            studentName: getLatestStudentName(seat.studentId, seat.studentName)
          });
        }
      });

      switch (template) {
        case 'individual':
          // 개별 배치 - 모든 좌석 떨어뜨림
          for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
              const seatKey = `${row}-${col}`;
              newSeats[seatKey] = {
                studentId: null,
                studentName: null,
                isDisabled: false,
                position: { row, col },
                bidAmount: null,
                bidTime: null,
                seatTax: null,
                groupId: undefined // 그룹 없음
              };
            }
          }
          break;

        case 'pair':
          // 짝꿍 배치 - 2명씩 가로로 붙임
          for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
              const seatKey = `${row}-${col}`;
              const groupId = `pair-${row}-${Math.floor(col / 2)}`; // 2개씩 묶기
              newSeats[seatKey] = {
                studentId: null,
                studentName: null,
                isDisabled: false,
                position: { row, col },
                bidAmount: null,
                bidTime: null,
                seatTax: null,
                groupId
              };
            }
          }
          break;

        case 'group4':
          // 4인 모둠 - 2x2로 배치
          for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
              const seatKey = `${row}-${col}`;
              const groupRow = Math.floor(row / 2);
              const groupCol = Math.floor(col / 2);
              const groupId = `group4-${groupRow}-${groupCol}`;
              newSeats[seatKey] = {
                studentId: null,
                studentName: null,
                isDisabled: false,
                position: { row, col },
                bidAmount: null,
                bidTime: null,
                seatTax: null,
                groupId
              };
            }
          }
          break;

        case 'group6':
          // 6인 모둠 - 2x3으로 배치
          for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
              const seatKey = `${row}-${col}`;
              const groupRow = Math.floor(row / 2);
              const groupCol = Math.floor(col / 3);
              const groupId = `group6-${groupRow}-${groupCol}`;
              newSeats[seatKey] = {
                studentId: null,
                studentName: null,
                isDisabled: false,
                position: { row, col },
                bidAmount: null,
                bidTime: null,
                seatTax: null,
                groupId
              };
            }
          }
          break;

        case 'u-shape':
          // ㄷ자 배치
          for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
              const seatKey = `${row}-${col}`;
              // 가운데 비우기
              const isCenter = row > 0 && row < rows - 1 && col > 0 && col < cols - 1;
              newSeats[seatKey] = {
                studentId: null,
                studentName: null,
                isDisabled: isCenter,
                position: { row, col },
                bidAmount: null,
                bidTime: null,
                seatTax: null
              };
            }
          }
          break;

        case 'square':
          // ㅁ자 배치
          for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
              const seatKey = `${row}-${col}`;
              // 가운데 완전히 비우기
              const isCenter = row >= 1 && row <= rows - 2 && col >= 1 && col <= cols - 2;
              newSeats[seatKey] = {
                studentId: null,
                studentName: null,
                isDisabled: isCenter,
                position: { row, col },
                bidAmount: null,
                bidTime: null,
                seatTax: null
              };
            }
          }
          break;
      }

      // 학생 재배정 (활성화된 좌석에만)
      const activeSeats = Object.entries(newSeats).filter(([_, seat]) => !seat.isDisabled);
      studentAssignments.forEach((student, index) => {
        if (index < activeSeats.length) {
          const [seatKey] = activeSeats[index];
          newSeats[seatKey].studentId = student.studentId;
          newSeats[seatKey].studentName = student.studentName;
        }
      });

      // Firestore 업데이트
      const docRef = isReservationMode ? 'reservation' : 'current';
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: newSeats,
        'settings.layoutTemplate': template,
        updatedAt: new Date()
      });

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? { 
          ...prev, 
          seats: newSeats,
          settings: { ...prev.settings, layoutTemplate: template }
        } : null);
      } else {
        setSeatingArrangement(prev => prev ? { 
          ...prev, 
          seats: newSeats,
          settings: { ...prev.settings, layoutTemplate: template }
        } : null);
      }

      toast.success(`${getTemplateName(template)} 배치가 적용되었습니다.`);
    } catch (err) {
      console.error('Error applying layout template:', err);
      setError('배치 템플릿 적용 중 오류가 발생했습니다.');
    }
  };

  const getTemplateName = (template: string) => {
    const names: Record<string, string> = {
      'individual': '개별',
      'pair': '짝꿍',
      'group4': '4인 모둠',
      'group6': '6인 모둠',
      'u-shape': 'ㄷ자',
      'square': 'ㅁ자'
    };
    return names[template] || template;
  };

  // 좌석 배치 게시
  const handlePublishArrangement = async () => {
    if (!seatingArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다. 다시 로그인해주세요.');
        return;
      }

      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, 'current');
      await updateDoc(arrangementRef, {
        status: 'published',
        updatedAt: new Date()
      });

      setSeatingArrangement(prev => prev ? {
        ...prev,
        status: 'published'
      } : null);
      
      toast.success('자리 배치가 게시되었습니다.');
    } catch (err) {
      console.error('Error publishing arrangement:', err);
      setError('자리 배치를 게시하는 중 오류가 발생했습니다.');
    }
  };

  // 예약 자리표 삭제
  const handleDeleteReservationArrangement = async () => {
    if (!currentUser || !isTeacher(currentUser)) return;

    if (!window.confirm('예약 자리표를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다. 다시 로그인해주세요.');
        return;
      }

      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, 'reservation');
      await deleteDoc(arrangementRef);

      setReservationArrangement(null);
      if (isReservationMode) {
        setIsReservationMode(false);
      }
      
      toast.success('예약 자리표가 삭제되었습니다.');
    } catch (err) {
      console.error('Error deleting reservation arrangement:', err);
      setError('예약 자리표를 삭제하는 중 오류가 발생했습니다.');
    }
  };

  /** 현재 자리표: Firestore `current` 문서를 좌석 없이 두고, 시작 화면에서 행·열 후 「시작」으로 칸 생성 */
  const handleResetCurrentSeatingArrangement = async () => {
    if (!currentUser || !isTeacher(currentUser)) return;
    if (isReservationMode) return;
    if (!seatingArrangement || seatingArrangement.status === 'published') {
      toast.error('게시된 자리표는 게시 취소 후에만 삭제·초기화할 수 있습니다.');
      return;
    }

    if (
      !window.confirm(
        '현재 자리표를 삭제할까요?\n학생 배정·자리세·비활성·배치 템플릿 등이 모두 사라지고, 다음 화면에서 행·열을 정한 뒤 「시작」으로 빈 칸을 만듭니다.'
      )
    ) {
      return;
    }

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다. 다시 로그인해주세요.');
        return;
      }

      const newArrangementData: SeatingArrangement = {
        id: 'current',
        name: '현재 좌석배치도',
        seats: {},
        settings: {
          rows: 4,
          cols: 6,
          isExamMode: false,
          isPairedSeating: false,
          boardPosition: 'top',
          enabled: false,
          startDate: null,
          endDate: null,
          reservationStartDate: null,
          reservationEndDate: null,
          reservationType: 'item'
        },
        status: 'draft',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, 'current');
      await setDoc(arrangementRef, {
        ...newArrangementData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      setSeatingArrangement(newArrangementData);
      setGridRows(4);
      setGridCols(6);
      setIsEditLayoutMode(false);
      setIsSeatTaxMode(false);
      setIsDisableMode(false);
      toast.success('자리표를 비웠습니다. 시작에서 행·열을 정한 뒤 자리 칸을 만들 수 있습니다.');
    } catch (err) {
      console.error('Error resetting current seating arrangement:', err);
      setError('자리표 초기화 중 오류가 발생했습니다.');
      toast.error('자리표 초기화 중 오류가 발생했습니다.');
    }
  };

  // 좌석 배치 게시 취소
  const handleUnpublishArrangement = async () => {
    if (!seatingArrangement || !currentUser || !isTeacher(currentUser)) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다. 다시 로그인해주세요.');
        return;
      }

      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, 'current');
      await updateDoc(arrangementRef, {
        status: 'draft',
        updatedAt: new Date()
      });

      setSeatingArrangement(prev => prev ? {
        ...prev,
        status: 'draft'
      } : null);
      
      toast.success('자리 배치 게시가 취소되었습니다.');
    } catch (err) {
      console.error('Error unpublishing arrangement:', err);
      setError('자리 배치 게시를 취소하는 중 오류가 발생했습니다.');
    }
  };

  // 학생 목록을 번호 순으로 정렬
  const sortedStudents = [...students].sort((a, b) => {
    const numA = a.number || 0;
    const numB = b.number || 0;
    return numA - numB;
  });

  const getLatestStudentName = (studentId: string | null, fallbackName?: string | null) => {
    if (!studentId) return fallbackName ?? null;
    const sid = String(studentId).trim();
    const matchedStudent = students.find(student => String(student.uid).trim() === sid);
    return matchedStudent?.name ?? fallbackName ?? null;
  };

  /** 교사 배정(studentId) 또는 학생 예약(uid) */
  const getSeatOccupantId = (seat: Seat | undefined): string | null => {
    if (!seat) return null;
    const id = seat.studentId ?? seat.uid;
    if (id == null || String(id).trim() === '') return null;
    return String(id).trim();
  };

  /** 배정 전: A01 형식. 배정 후(학생이 있으면): 학생 번호(예: 3번) */
  const getSeatPositionLabel = (row: number, col: number, seat: Seat | undefined) => {
    const seatCode = String.fromCharCode(65 + row) + String(col + 1).padStart(2, '0');
    const occId = getSeatOccupantId(seat);
    if (!occId) {
      return seatCode;
    }
    const sid = occId;
    const matched = students.find(s => String(s.uid).trim() === sid);
    if (!matched) {
      return seatCode;
    }
    const raw = matched.number as unknown;
    const num =
      raw === null || raw === undefined || raw === ''
        ? NaN
        : typeof raw === 'number'
          ? raw
          : Number(raw);
    if (Number.isFinite(num) && num > 0) {
      return `${Math.floor(num)}번`;
    }
    return seatCode;
  };

  /** 자리 칩 상단(번호/A01): 글자 크기·자릿수마다 비슷하게 보이도록 (뷰포트 비례 크기와 동일 비율) */
  const seatPositionLabelStyle: React.CSSProperties = useMemo(
    () => ({
      fontSize: `${teacherSeatSizes.px(14)}px`,
      fontWeight: 600,
      fontFamily: 'Pretendard, sans-serif',
      lineHeight: `${teacherSeatSizes.px(18)}px`,
      textAlign: 'center',
      minWidth: `${teacherSeatSizes.px(44)}px`,
      fontVariantNumeric: 'tabular-nums',
      fontFeatureSettings: '"tnum"',
      display: 'inline-block',
      boxSizing: 'border-box'
    }),
    [teacherSeatSizes]
  );

  // 배정·예약이 있는 좌석 수 (학생 예약은 uid로 저장)
  const assignedSeatsCount = Object.values(currentArrangement?.seats || {}).filter(
    (seat) => getSeatOccupantId(seat) != null
  ).length;
  const totalSeatsCount = Object.values(currentArrangement?.seats || {}).filter(seat => !seat.isDisabled).length;

  // 좌석 간격 계산 함수 (가로)
  const getSeatMargin = (row: number, col: number) => {
    const seat = currentArrangement?.seats[`${row}-${col}`];
    const nextSeat = currentArrangement?.seats[`${row}-${col + 1}`];
    const template = currentArrangement?.settings.layoutTemplate;

    // 같은 그룹이면 간격 좁게, 다른 그룹이면 간격 넓게
    if (seat?.groupId && nextSeat?.groupId && seat.groupId === nextSeat.groupId) {
      return '4px'; // 같은 그룹 내 간격
    }
    
    if (template === 'individual') {
      return '12px'; // 개별 배치는 모두 동일한 간격
    }

    return '20px'; // 다른 그룹 간 간격
  };

  // 그리드 셀 더블클릭으로 좌석 추가
  const handleGridCellDoubleClick = async (row: number, col: number) => {
    if (!isEditLayoutMode) return;
    
    const seatKey = `${row}-${col}`;
    const seat = currentArrangement?.seats[seatKey];

    // 빈 셀 더블클릭 → 새 좌석 추가
    if (!seat) {
      await handleAddSeat(row, col);
    }
  };

  // 좌석 우클릭으로 삭제
  const handleSeatRightClick = async (e: React.MouseEvent, seatKey: string) => {
    e.preventDefault();
    if (!isEditLayoutMode) return;
    
    if (window.confirm('이 좌석을 삭제하시겠습니까?')) {
      await handleDeleteSeat(seatKey);
    }
  };

  /** 현재 자리 관리: 교사가 배정한 학생만 우클릭으로 배정 취소 (학생 목록으로 드래그와 동일) */
  const handleClearSeatAssignmentContext = async (e: React.MouseEvent, seatKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEditLayoutMode || isReservationMode || isDisableMode || isSeatTaxMode) return;
    if (!currentUser || !isTeacher(currentUser)) return;
    if (!currentArrangement || !seatingArrangement) return;
    if (seatingArrangement.status === 'published') {
      toast.error('게시된 자리표는 게시 취소 후에만 배정을 해제할 수 있습니다.');
      return;
    }
    const seat = currentArrangement.seats[seatKey];
    if (!seat || seat.isDisabled || !seat.studentId) return;

    if (!window.confirm('이 자리에서 학생 배정을 취소할까요?')) return;

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) return;

      const updatedSeats = { ...currentArrangement.seats };
      updatedSeats[seatKey] = {
        ...updatedSeats[seatKey],
        studentId: null,
        studentName: null
      };

      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, 'current');
      await updateDoc(arrangementRef, {
        seats: updatedSeats,
        updatedAt: new Date()
      });

      setSeatingArrangement((prev) => (prev ? { ...prev, seats: updatedSeats } : null));
      toast.success('좌석 배정을 취소했습니다.');
    } catch (err) {
      console.error('배정 취소 오류:', err);
      toast.error('배정 취소 중 오류가 발생했습니다.');
    }
  };

  // 그리드 렌더링 (편집 모드와 일반 모드 공통)
  const renderGrid = () => {
    const CELL_SIZE = teacherSeatSizes.cellPx;
    const CHIP_PX = teacherSeatSizes.chipPx;
    const CHIP_RADIUS = teacherSeatSizes.chipBorderRadiusPx;
    const spx = teacherSeatSizes.px;

    return (
      <>
        {Array.from({ length: gridRows }).map((_, row) => (
          <div 
            key={`grid-row-${row}`}
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: `${gridCols * CELL_SIZE}px`,
              minWidth: `${gridCols * CELL_SIZE}px`,
              flexShrink: 0
            }}
          >
            {Array.from({ length: gridCols }).map((_, col) => {
              const seatKey = `${row}-${col}`;
              const seat = currentArrangement?.seats[seatKey];
              const seatDisplayName = getLatestStudentName(
                getSeatOccupantId(seat) ?? null,
                seat?.studentName ?? seat?.name ?? null
              );
              const hasOccupant = Boolean(getSeatOccupantId(seat));
              const isStudentReservation = Boolean(seat?.isReserved || (seat?.uid && String(seat.uid).trim()));

              return (
                <Droppable 
                  key={`grid-cell-${row}-${col}`} 
                  droppableId={isEditLayoutMode ? `grid-cell-${row}-${col}` : seatKey}
                  isDropDisabled={!isEditLayoutMode && seat?.isDisabled}
                >
                  {(provided, snapshot) => {
                    const draggingFromStudentList = activeDragId?.startsWith('student-list-');
                    const isStudentListHover =
                      draggingFromStudentList &&
                      snapshot.isDraggingOver &&
                      !(seat?.isDisabled && !isEditLayoutMode);

                    /** grid 셀의 placeholder가 flex 레이아웃에 영향을 주지 않도록 항상 숨김 처리 */
                    const hiddenDropPlaceholder = (
                      <div
                        style={{
                          position: 'absolute',
                          width: 0,
                          height: 0,
                          overflow: 'hidden',
                          opacity: 0,
                          pointerEvents: 'none'
                        }}
                        aria-hidden
                      >
                        {provided.placeholder}
                      </div>
                    );

                    const cellStyle: React.CSSProperties = {
                      width: `${CELL_SIZE}px`,
                      height: `${CELL_SIZE}px`,
                      minWidth: `${CELL_SIZE}px`,
                      maxWidth: `${CELL_SIZE}px`,
                      minHeight: `${CELL_SIZE}px`,
                      maxHeight: `${CELL_SIZE}px`,
                      flexShrink: 0,
                      border: isEditLayoutMode ? '1px solid #D8D8D8' : 'none',
                      background: isEditLayoutMode
                        ? (snapshot.isDraggingOver && !draggingFromStudentList ? '#FFE5D3' : '#FAFAFA')
                        : 'transparent',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      transition: 'background 0.2s',
                      boxSizing: 'border-box',
                      cursor: isEditLayoutMode
                        ? 'pointer'
                        : (isDisableMode || (isSeatTaxMode && seat) ? 'pointer' : 'default')
                    };

                    if (!seat) {
                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          onDoubleClick={isEditLayoutMode ? () => handleGridCellDoubleClick(row, col) : undefined}
                          onClick={
                            isEditLayoutMode
                              ? undefined
                              : isDisableMode
                                ? () => handleToggleSeatDisabled(row, col)
                                : isSeatTaxMode && seat
                                  ? () => openSeatTaxDialog(seatKey)
                                  : undefined
                          }
                          style={cellStyle}
                        >
                          {hiddenDropPlaceholder}
                        </div>
                      );
                    }

                    const seatChipBody = (
                      <>
                        <div
                          style={{
                            ...seatPositionLabelStyle,
                            color: '#4C4C4C'
                          }}
                        >
                          {getSeatPositionLabel(row, col, seat)}
                        </div>
                        {seatDisplayName && (
                          <div
                            style={{
                              fontSize: `${spx(13)}px`,
                              color: '#4C4C4C',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              marginTop: '1px',
                              lineHeight: `${spx(17)}px`,
                              textAlign: 'center',
                              maxWidth: `${spx(68)}px`,
                              wordBreak: 'keep-all'
                            }}
                          >
                            {seatDisplayName}
                          </div>
                        )}
                        {seat.isDisabled && (
                          <div
                            style={{
                              fontSize: `${spx(9)}px`,
                              color: '#BABABA',
                              fontFamily: 'Pretendard',
                              fontWeight: '400'
                            }}
                          >
                            비활성화
                          </div>
                        )}
                        {seat.seatTax != null && (
                          <div
                            style={{
                              fontSize: `${spx(9)}px`,
                              color: 'var(--Color-fill-brand-color, #FF6F0F)',
                              fontFamily: 'Pretendard',
                              fontWeight: '600',
                              marginTop: '1px',
                              lineHeight: `${spx(12)}px`,
                              textAlign: 'center'
                            }}
                          >
                            자리세 {seat.seatTax.toLocaleString('ko-KR')}
                          </div>
                        )}
                        {isReservationMode && isStudentReservation && hasOccupant && (
                          <div
                            style={{
                              fontSize: `${spx(9)}px`,
                              color: 'var(--Color-fill-brand-color, #FF6F0F)',
                              fontFamily: 'Pretendard',
                              fontWeight: '600',
                              marginTop: '2px',
                              lineHeight: `${spx(12)}px`,
                              textAlign: 'center'
                            }}
                          >
                            예약
                          </div>
                        )}
                      </>
                    );

                    const chipBaseStyle: React.CSSProperties = {
                      width: `${CHIP_PX}px`,
                      height: `${CHIP_PX}px`,
                      minWidth: `${CHIP_PX}px`,
                      maxWidth: `${CHIP_PX}px`,
                      overflow: 'hidden',
                      background: seat.isDisabled ? '#EDEDED' : hasOccupant ? '#FFF1E7' : '#F8F8F8',
                      borderRadius: `${CHIP_RADIUS}px`,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxShadow: isEditLayoutMode ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                      zIndex: 1
                    };
                    const seatDraggableId = isEditLayoutMode ? `seat-${seatKey}` : (getSeatOccupantId(seat) || seatKey);
                    const isSeatHoverDropTarget =
                      Boolean(activeDragId) &&
                      activeDragId !== seatDraggableId &&
                      snapshot.isDraggingOver &&
                      !seat.isDisabled;

                    return (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      onDoubleClick={isEditLayoutMode ? () => handleGridCellDoubleClick(row, col) : undefined}
                      onClick={
                        isEditLayoutMode
                          ? undefined
                          : isDisableMode
                            ? () => handleToggleSeatDisabled(row, col)
                            : isSeatTaxMode && seat
                              ? () => openSeatTaxDialog(seatKey)
                              : undefined
                      }
                      style={cellStyle}
                    >
                      {/*
                        key를 draggableId와 맞춘다. 학생을 교환하면 두 칸의 draggableId가 서로 바뀌는데,
                        같은 Draggable이 id만 바꾸면 @hello-pangea/dnd 등록부가 한쪽 id를 덮어써서
                        그 학생은 다시 드래그되지 않는다. id가 바뀌면 새로 마운트해 등록을 다시 하게 한다.
                      */}
                      <Draggable
                        key={seatDraggableId}
                        draggableId={seatDraggableId}
                        index={0}
                        isDragDisabled={
                          !isEditLayoutMode &&
                          (currentArrangement?.status === 'published' || isSeatTaxMode)
                        }
                      >
                        {(dragProvided, dragSnapshot) => {
                          const dndStyle = dragProvided.draggableProps.style ?? {};
                          const suppressPeerSeatDisplacement =
                            Boolean(activeDragId) && activeDragId !== seatDraggableId && !dragSnapshot.isDragging;
                          return (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            onContextMenu={(ev) => {
                              if (isEditLayoutMode) {
                                void handleSeatRightClick(ev, seatKey);
                                return;
                              }
                              void handleClearSeatAssignmentContext(ev, seatKey);
                            }}
                            title={
                              isEditLayoutMode
                                ? undefined
                                : !isReservationMode &&
                                    !isDisableMode &&
                                    !isSeatTaxMode &&
                                    seat?.studentId &&
                                    seatingArrangement?.status !== 'published'
                                  ? '우클릭: 배정 취소 (또는 학생을 왼쪽 목록으로 드래그)'
                                  : undefined
                            }
                            style={{
                              ...chipBaseStyle,
                              background: seat.isDisabled
                                ? '#EDEDED'
                                : isSeatHoverDropTarget
                                  ? 'rgba(255, 111, 15, 0.2)'
                                  : hasOccupant
                                    ? '#FFF1E7'
                                    : '#F8F8F8',
                              cursor: dragSnapshot.isDragging
                                ? 'grabbing'
                                : (isEditLayoutMode ? 'grab' : (isSeatTaxMode ? 'pointer' : 'default')),
                              boxShadow: dragSnapshot.isDragging
                                ? '0px 8px 16px rgba(255, 111, 15, 0.4)'
                                : isSeatHoverDropTarget
                                  ? '0 0 0 2px var(--Color-fill-brand-color, #FF6F0F)'
                                : (isEditLayoutMode ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 'none'),
                              zIndex: dragSnapshot.isDragging ? 1000 : 1,
                              ...dndStyle,
                              ...(suppressPeerSeatDisplacement ? { transform: 'none', transition: 'none' } : {}),
                              ...(dragSnapshot.isDragging && dndStyle.transform && {
                                transform: dndStyle.transform
                              })
                            }}
                          >
                            {seatChipBody}
                          </div>
                          );
                        }}
                      </Draggable>
                      {hiddenDropPlaceholder}
                    </div>
                    );
                  }}
                </Droppable>
              );
            })}
          </div>
        ))}
      </>
    );
  };

  // 좌석 렌더링 함수
  const renderSeat = (row: number, col: number) => {
    const seatKey = `${row}-${col}`;
    const seat = currentArrangement?.seats[seatKey];
    const seatDisplayName = getLatestStudentName(seat?.studentId ?? null, seat?.studentName);
    const seatTopLabel = getSeatPositionLabel(row, col, seat);

    const getSeatStyle = () => {
      if (seat?.isDisabled) {
        return {
          background: 'var(--Color-fill-disable, #EDEDED)',
          color: 'var(--Color-text-minor, #BABABA)'
        };
      }
      if (seat?.studentId) {
        return {
          background: '#FFF1E7',
          color: 'var(--Color-text-assistive-strong, #4C4C4C)'
        };
      }
      if (seat) {
        return {
          background: '#F8F8F8',
          color: 'var(--Color-text-assistive-strong, #4C4C4C)'
        };
      }
      // 편집 모드에서 빈 칸
      return {
        background: 'transparent',
        color: 'var(--Color-text-minor, #BABABA)'
      };
    };

    const style = getSeatStyle();

     const marginRight = getSeatMargin(row, col);

     return (
       <Droppable key={seatKey} droppableId={seatKey} isDropDisabled={seat?.isDisabled}>
         {(provided) => (
           <div
             ref={provided.innerRef}
             {...provided.droppableProps}
             onClick={isDisableMode ? () => handleToggleSeatDisabled(row, col) : undefined}
             style={{
               width: `${teacherSeatSizes.chipPx}px`,
               height: `${teacherSeatSizes.chipPx}px`,
               padding: '16px 20px',
               background: style.background,
               borderRadius: `${teacherSeatSizes.chipBorderRadiusPx}px`,
               display: 'flex',
               justifyContent: 'center',
               alignItems: 'center',
               cursor: isDisableMode ? 'pointer' : (seat?.isDisabled ? 'not-allowed' : 'pointer'),
               marginRight: marginRight
             }}
           >
             {seat?.studentId ? (
               <Draggable
                 key={seat.studentId}
                 draggableId={seat.studentId}
                 index={0}
                 isDragDisabled={seatingArrangement?.status === 'published'}
               >
                 {(dragProvided) => (
                   <div
                     ref={dragProvided.innerRef}
                     {...dragProvided.draggableProps}
                     {...dragProvided.dragHandleProps}
                     style={{
                       display: 'flex',
                       flexDirection: 'column',
                       alignItems: 'center',
                       justifyContent: 'center',
                       gap: '1px'
                     }}
                   >
                     <div
                       style={{
                         ...seatPositionLabelStyle,
                         color: style.color
                       }}
                     >
                       {seatTopLabel}
                     </div>
                     <div style={{
                       color: style.color,
                       fontSize: `${teacherSeatSizes.px(13)}px`,
                       fontFamily: 'Pretendard',
                       fontWeight: '500',
                       lineHeight: `${teacherSeatSizes.px(17)}px`,
                       wordWrap: 'break-word',
                       textAlign: 'center',
                       maxWidth: `${teacherSeatSizes.px(68)}px`
                     }}>
                       {seatDisplayName}
                     </div>
                     {seat?.isDisabled && (
                       <div style={{
                         fontSize: `${teacherSeatSizes.px(9)}px`,
                         color: style.color,
                         fontFamily: 'Pretendard',
                         fontWeight: '400',
                         lineHeight: `${teacherSeatSizes.px(12)}px`
                       }}>
                         비활성화
                       </div>
                     )}
                   </div>
                 )}
               </Draggable>
             ) : (
               <div style={{
                 display: 'flex',
                 flexDirection: 'column',
                 alignItems: 'center',
                 justifyContent: 'center',
                 gap: '1px'
               }}>
                 <div
                   style={{
                     ...seatPositionLabelStyle,
                     color: style.color
                   }}
                 >
                   {seatTopLabel}
                 </div>
                 {seat?.isDisabled && (
                   <div style={{
                     fontSize: `${teacherSeatSizes.px(9)}px`,
                     color: style.color,
                     fontFamily: 'Pretendard',
                     fontWeight: '400',
                     lineHeight: `${teacherSeatSizes.px(12)}px`
                   }}>
                     비활성화
                   </div>
                 )}
               </div>
             )}
             {provided.placeholder}
           </div>
         )}
       </Droppable>
     );
  };

  // 자리 배치 설정 편집 (기존 자리표의 설정만 업데이트)
  const handleUpdateSeatSettings = async (data: {
    startDate: string;
    endDate: string;
    rows: number;
    cols: number;
    isPairedSeating: boolean;
    boardPosition: 'top' | 'bottom';
  }) => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;
    
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) {
      setError('필수 정보가 누락되었습니다.');
      return;
    }

    const { rows, cols, isPairedSeating, boardPosition, startDate, endDate } = data;

    // 날짜 문자열을 Date 객체로 변환
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj = endDate ? new Date(endDate) : null;

    try {
      const docRef = isReservationMode ? 'reservation' : selectedPeriod;
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      
      // settings만 업데이트
      await updateDoc(arrangementRef, {
        'settings.rows': rows,
        'settings.cols': cols,
        'settings.isPairedSeating': isPairedSeating,
        'settings.boardPosition': boardPosition,
        'settings.startDate': startDateObj ? Timestamp.fromDate(startDateObj) : null,
        'settings.endDate': endDateObj ? Timestamp.fromDate(endDateObj) : null,
        updatedAt: new Date()
      });

      // 로컬 state 업데이트
      const updatedSettings = {
        ...currentArrangement.settings,
        rows,
        cols,
        isPairedSeating,
        boardPosition,
        startDate: startDateObj,
        endDate: endDateObj
      };

      if (isReservationMode) {
        setReservationArrangement(prev => prev ? {
          ...prev,
          settings: updatedSettings
        } : null);
      } else {
        setSeatingArrangement(prev => prev ? {
          ...prev,
          settings: updatedSettings
        } : null);
      }

      // gridRows, gridCols도 업데이트
      setGridRows(rows);
      setGridCols(cols);
      
      toast.success('자리 배치 설정이 업데이트되었습니다.');
      setIsEditSeatSettingsDialogOpen(false);
    } catch (error) {
      console.error('자리 배치 설정 업데이트 중 오류 발생:', error);
      setError('자리 배치 설정 업데이트 중 오류가 발생했습니다.');
    }
  };

  // 그리드 크기 변경 핸들러 (Firestore에 저장)
  const handleGridSizeChange = async (newRows: number, newCols: number) => {
    if (!currentArrangement || !currentUser || !isTeacher(currentUser)) return;
    
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) return;

    try {
      const docRef = isReservationMode ? 'reservation' : selectedPeriod;
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      
      await updateDoc(arrangementRef, {
        'settings.rows': newRows,
        'settings.cols': newCols,
        updatedAt: new Date()
      });

      // 로컬 state도 업데이트
      setGridRows(newRows);
      setGridCols(newCols);

      // seatingArrangement state도 업데이트
      if (isReservationMode) {
        setReservationArrangement(prev => prev ? {
          ...prev,
          settings: { ...prev.settings, rows: newRows, cols: newCols }
        } : null);
      } else {
        setSeatingArrangement(prev => prev ? {
          ...prev,
          settings: { ...prev.settings, rows: newRows, cols: newCols }
        } : null);
      }
    } catch (err) {
      console.error('Error updating grid size:', err);
      toast.error('그리드 크기 업데이트 중 오류가 발생했습니다.');
    }
  };

  /** 좌석을 모두 삭제해 seats가 비었을 때: 행·열에 맞춰 빈 칸 생성 */
  const handleCreateCurrentSeatsFromEmpty = async () => {
    if (!currentUser || !isTeacher(currentUser)) return;
    if (isReservationMode) return;
    if (!seatingArrangement || Object.keys(seatingArrangement.seats).length > 0) return;

    const rows = gridRows;
    const cols = gridCols;
    if (rows < 1 || cols < 1) {
      toast.error('행과 열은 1 이상이어야 합니다.');
      return;
    }

    const initialSeats: Record<string, Seat> = {};
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const seatKey = `${row}-${col}`;
        initialSeats[seatKey] = {
          studentId: null,
          studentName: null,
          isDisabled: false,
          position: { row, col },
          bidAmount: null,
          bidTime: null,
          seatTax: null
        };
      }
    }

    try {
      const { classId, schoolId } = currentUser;
      if (!classId || !schoolId) {
        setError('필수 정보가 누락되었습니다.');
        return;
      }

      const docRef = selectedPeriod;
      const arrangementRef = doc(db, `schools/${schoolId}/classes/${classId}/seatingArrangements`, docRef);
      await updateDoc(arrangementRef, {
        seats: initialSeats,
        'settings.rows': rows,
        'settings.cols': cols,
        updatedAt: new Date()
      });

      setSeatingArrangement(prev =>
        prev
          ? {
              ...prev,
              seats: initialSeats,
              settings: { ...prev.settings, rows, cols },
              updatedAt: new Date()
            }
          : null
      );

      toast.success('빈 자리 칸을 만들었습니다. 학생을 배치해 보세요.');
    } catch (err) {
      console.error('Error creating seats from empty:', err);
      toast.error('자리 생성 중 오류가 발생했습니다.');
    }
  };

  return (
    <DragDropContext
      onDragStart={(start) => setActiveDragId(start.draggableId)}
      onDragEnd={(result) => {
        setActiveDragId(null);
        void handleDragEnd(result);
      }}
    >
      <div
        data-name="seat-management-root"
        className="seat-management-root"
        style={{ 
        width: '100%', 
        height: '100%', 
        fontFamily: 'Pretendard'
      }}>
        <div
          data-name="seat-management-container"
          className="seat-management-container"
          style={{
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: '10px'
        }}>
          <div
            data-name="seat-management-content"
            className="seat-management-content"
            style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '32px'
          }}>
            {/* 제목 영역 */}
          <div
            data-name="seat-management-header"
            className="seat-management-header"
            style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '8px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '16px',
              fontFamily: 'Pretendard',
              fontWeight: '400',
              lineHeight: '24px',
              wordWrap: 'break-word'
            }}>
              {currentUser?.homeroomClass?.grade}학년 {currentUser?.homeroomClass?.class}반
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '32px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '48px',
              wordWrap: 'break-word'
            }}>
              자리 관리
            </div>
          </div>

          {/* 탭 및 날짜 선택 영역 */}
          <div
            data-name="seat-management-controls"
            className="seat-management-controls"
            style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '20px'
          }}>
            <div
              data-name="seat-management-tab-row"
              className="seat-management-tab-row"
              style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '16px'
              }}>
                <Button
                  variant={!isReservationMode ? 'primary' : 'secondary'}
                  onClick={() => setIsReservationMode(false)}
                >
                  현재 자리 관리
                </Button>
                <Button
                  variant={isReservationMode ? 'primary' : 'secondary'}
                  onClick={() => setIsReservationMode(true)}
                >
                  예약 자리 관리
                </Button>
              </div>
              
              {/* 기간 선택 드롭다운 + 자리 배치표 출력 */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                <DropdownButton
                  value={selectedPeriod}
                  onChange={(value: string) => setSelectedPeriod(value)}
                  options={[
                    { 
                      value: 'current', 
                      label: seatingArrangement?.settings.startDate && seatingArrangement?.settings.endDate 
                        ? `${format(seatingArrangement.settings.startDate, 'yyyy.MM.dd')}~${format(seatingArrangement.settings.endDate, 'yyyy.MM.dd')}`
                        : '현재 자리표'
                    },
                    ...allArrangements.map((arrangement) => {
                      const startDate = arrangement.settings.startDate;
                      const endDate = arrangement.settings.endDate;
                      if (startDate && endDate) {
                        return {
                          value: arrangement.id,
                          label: `${format(startDate, 'yyyy.MM.dd')}~${format(endDate, 'yyyy.MM.dd')}`
                        };
                      }
                      return {
                        value: arrangement.id,
                        label: arrangement.name || '자리표'
                      };
                    })
                  ]}
                  width="220px"
                  height="40px"
                />
                {!isReservationMode && seatingArrangement && (
                  <Button
                    variant="secondary"
                    onClick={() => window.open('/class/kiosk/connect', '_blank', 'noopener,noreferrer')}
                  >
                    태블릿 연동
                  </Button>
                )}
                {currentArrangement && (
                  <Button variant="secondary" onClick={handlePrintSeatingArrangement}>
                    자리 배치표 출력
                  </Button>
                )}
              </div>
            </div>

             {/* 메인 컨텐츠 카드 */}
             {isReservationMode && !reservationArrangement ? (
               /* 예약 자리 관리 시작 화면 */
               <div
                 data-name="seat-management-reservation-empty"
                 className="seat-management-print-target"
                 ref={seatingPrintRef}
                 style={{
                 width: '100%',
                 height: 'clamp(500px, 70vh, min(88vh, 840px))',
                 padding: '1rem 1.25rem',
                 background: 'var(--Color-background-normal, white)',
                 borderRadius: '1rem',
                 display: 'flex',
                 overflow: 'hidden'
               }}>
                 {/* 왼쪽: 학생 목록 */}
                 <div style={{
                   display: 'flex',
                   justifyContent: 'flex-start',
                   alignItems: 'stretch',
                   gap: '1.5rem',
                   width: '100%',
                   height: '100%',
                   minHeight: 0,
                   overflow: 'hidden',
                   boxSizing: 'border-box'
                 }}>
                  <div style={{
                    width: '13.5rem',
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: '1.25rem'
                  }}>
                    {/* 잔여 자리 카드 */}
                    <div style={{
                       width: '100%',
                       background: 'var(--Color-background-sub, #F8F8F8)',
                       borderRadius: '0.75rem',
                       display: 'flex',
                       flexDirection: 'column',
                       alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      height: '100%'
                     }}>
                       <div style={{
                         width: '100%',
                         padding: '16px',
                         borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                         display: 'flex',
                         flexDirection: 'column',
                         alignItems: 'flex-start',
                         justifyContent: 'flex-start',
                         gap: '4px'
                       }}>
                         <div style={{
                           width: '100%',
                           color: 'var(--Color-text-normal, #161616)',
                           fontSize: '13px',
                           fontFamily: 'Pretendard',
                           fontWeight: '500',
                           lineHeight: '19.50px',
                           wordWrap: 'break-word'
                         }}>
                           잔여 자리
                         </div>
                         <div style={{
                           width: '100%',
                           display: 'flex',
                           flexDirection: 'row',
                           justifyContent: 'flex-start',
                           alignItems: 'flex-end',
                           gap: '4px'
                         }}>
                           <div style={{
                             height: '38px',
                             color: 'var(--Color-text-normal, #161616)',
                             fontSize: '28px',
                             fontFamily: 'Pretendard',
                             fontWeight: '500',
                             lineHeight: '42px',
                             whiteSpace: 'nowrap',
                             flexShrink: 0
                           }}>
                             {reservationRows * reservationCols}석
                           </div>
                           <div style={{
                             color: 'var(--Color-text-assistive, #989898)',
                             fontSize: '18px',
                             fontWeight: '500',
                             lineHeight: '27px',
                             whiteSpace: 'nowrap',
                             flexShrink: 0
                           }}>
                             /{reservationRows * reservationCols}석
                           </div>
                         </div>
                       </div>

                       {/* 학생 목록 */}
                      <div style={{
                        width: '100%',
                        flex: 1,
                        minHeight: 0,
                        padding: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        gap: '12px'
                      }}>
                         <div style={{
                           width: '100%',
                           color: 'var(--Color-text-normal, #161616)',
                           fontSize: '14px',
                           fontFamily: 'Pretendard',
                           fontWeight: '600',
                           lineHeight: '21px',
                           wordWrap: 'break-word'
                         }}>
                           학생 목록
                         </div>
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          gap: '8px',
                          flex: 1,
                          minHeight: 0,
                          overflowY: 'auto'
                        }}>
                           {sortedStudents.map((student) => (
                             <div
                               key={student.uid}
                               style={{
                                 width: '100%',
                                 minHeight: '40px',
                                 padding: '8px 12px',
                                 borderRadius: '8px',
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                                 background: 'var(--Color-fill-hover-2-dg, #EDEDED)'
                               }}
                             >
                               <div style={{
                                 flex: '1 1 0',
                                 display: 'flex',
                                 justifyContent: 'flex-start',
                                 alignItems: 'center',
                                 gap: '4px'
                               }}>
                                 <div style={{
                                   color: 'var(--Color-text-normal, #161616)',
                                   fontSize: '14px',
                                   fontFamily: 'Pretendard',
                                   fontWeight: '500',
                                   lineHeight: '21px',
                                   wordWrap: 'break-word'
                                 }}>
                                   {student.number}번
                                 </div>
                                 <div style={{
                                   flex: '1 1 0',
                                   color: 'var(--Color-text-normal, #161616)',
                                   fontSize: '14px',
                                   fontFamily: 'Pretendard',
                                   fontWeight: '500',
                                   lineHeight: '21px',
                                   wordWrap: 'break-word'
                                 }}>
                                   {student.name}
                                 </div>
                               </div>
                             </div>
                           ))}
                         </div>
                       </div>
                     </div>
                   </div>

                 {/* 오른쪽: 예약 시작 설정 폼 */}
                 <div style={{
                   flex: '1',
                   minHeight: 0,
                   overflow: 'auto',
                   display: 'flex',
                   flexDirection: 'column',
                   alignItems: 'center',
                   justifyContent: 'center',
                   gap: '28px',
                   padding: '20px'
                 }}>
                   {/* 헤더 */}
                   <div style={{
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     justifyContent: 'center',
                     gap: '8px'
                   }}>
                     <div style={{
                       textAlign: 'center',
                       color: 'var(--Color-text-normal, #161616)',
                       fontSize: '20px',
                       fontFamily: 'Pretendard',
                       fontWeight: '600',
                       lineHeight: '30px',
                       wordWrap: 'break-word'
                     }}>
                       예약 자리 관리 시작하기
                     </div>
                     <div style={{
                       textAlign: 'center',
                       color: 'var(--Color-text-assistive, #989898)',
                       fontSize: '16px',
                       fontFamily: 'Pretendard',
                       fontWeight: '500',
                       lineHeight: '24px',
                       wordWrap: 'break-word'
                     }}>
                       원하시는 반 자리 옵션을 설정해 주세요.
                     </div>
                   </div>

                   {/* 설정 폼 */}
                   <div style={{
                     display: 'flex',
                     flexDirection: 'column',
                     gap: '20px',
                     width: '100%',
                     maxWidth: '700px'
                   }}>
                   {/* 기간 설정 */}
                   <div style={{
                     display: 'flex',
                     justifyContent: 'flex-start',
                     alignItems: 'center',
                     gap: '40px'
                   }}>
                     <div style={{
                       width: '135px',
                       color: 'var(--Color-text-normal, #161616)',
                       fontSize: '16px',
                       fontFamily: 'Pretendard',
                       fontWeight: '600',
                       lineHeight: '24px',
                       wordWrap: 'break-word'
                     }}>
                       기간 설정
                     </div>
                     <div style={{
                       display: 'flex',
                       gap: '12px'
                     }}>
                       <InputField
                         type="date"
                         value={reservationStartDate || ''}
                         onChange={(value) => setReservationStartDate(value)}
                         placeholder="시작 날짜 선택"
                         width="240px"
                         height="48px"
                       />
                       <InputField
                         type="date"
                         value={reservationEndDate || ''}
                         onChange={(value) => setReservationEndDate(value)}
                         placeholder="종료 날짜 선택"
                         width="240px"
                         height="48px"
                       />
                     </div>
                   </div>

                   {/* 행 설정 */}
                   <div style={{
                     display: 'flex',
                     justifyContent: 'flex-start',
                     alignItems: 'center',
                     gap: '40px'
                   }}>
                     <div style={{
                       width: '135px',
                       color: 'var(--Color-text-normal, #161616)',
                       fontSize: '16px',
                       fontFamily: 'Pretendard',
                       fontWeight: '600',
                       lineHeight: '24px',
                       wordWrap: 'break-word'
                     }}>
                       행 설정
                     </div>
                     <div style={{
                       width: '492px',
                       display: 'flex',
                       padding: '12px 16px',
                       background: 'var(--Color-fill-white, white)',
                       borderRadius: '8px',
                       border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                       justifyContent: 'space-between',
                       alignItems: 'center',
                       gap: '8px'
                     }}>
                       <div
                         style={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           cursor: 'pointer',
                           width: '24px',
                           height: '24px'
                         }}
                         onClick={() => setReservationRows(Math.max(1, reservationRows - 1))}
                       >
                         <MinusIcon width={13.20} height={1.20} color="#0D0D0D" />
                       </div>
                       <div style={{
                         flex: '1 1 0',
                         textAlign: 'center',
                         color: 'var(--Color-text-normal, #161616)',
                         fontSize: '13px',
                         fontFamily: 'Pretendard',
                         fontWeight: '500',
                         lineHeight: '19.50px',
                         wordWrap: 'break-word'
                       }}>
                         {reservationRows}
                       </div>
                       <div
                         style={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           cursor: 'pointer',
                           width: '24px',
                           height: '24px'
                         }}
                         onClick={() => setReservationRows(reservationRows + 1)}
                       >
                         <PlusIcon width={13.20} height={13.20} fill="#0D0D0D" />
                       </div>
                     </div>
                   </div>

                   {/* 열 설정 */}
                   <div style={{
                     display: 'flex',
                     justifyContent: 'flex-start',
                     alignItems: 'center',
                     gap: '40px'
                   }}>
                     <div style={{
                       width: '135px',
                       color: 'var(--Color-text-normal, #161616)',
                       fontSize: '16px',
                       fontFamily: 'Pretendard',
                       fontWeight: '600',
                       lineHeight: '24px',
                       wordWrap: 'break-word'
                     }}>
                       열 설정
                     </div>
                     <div style={{
                       width: '492px',
                       display: 'flex',
                       padding: '12px 16px',
                       background: 'var(--Color-fill-white, white)',
                       borderRadius: '8px',
                       border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                       justifyContent: 'space-between',
                       alignItems: 'center',
                       gap: '8px'
                     }}>
                       <div
                         style={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           cursor: 'pointer',
                           width: '24px',
                           height: '24px'
                         }}
                         onClick={() => setReservationCols(Math.max(1, reservationCols - 1))}
                       >
                         <MinusIcon width={13.20} height={1.20} color="#0D0D0D" />
                       </div>
                       <div style={{
                         flex: '1 1 0',
                         textAlign: 'center',
                         color: 'var(--Color-text-normal, #161616)',
                         fontSize: '13px',
                         fontFamily: 'Pretendard',
                         fontWeight: '500',
                         lineHeight: '19.50px',
                         wordWrap: 'break-word'
                       }}>
                         {reservationCols}
                       </div>
                       <div
                         style={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           cursor: 'pointer',
                           width: '24px',
                           height: '24px'
                         }}
                         onClick={() => setReservationCols(reservationCols + 1)}
                       >
                         <PlusIcon width={13.20} height={13.20} fill="#0D0D0D" />
                       </div>
                     </div>
                   </div>

                   {/* 총 자리 수 */}
                   <div style={{
                     display: 'flex',
                     justifyContent: 'flex-start',
                     alignItems: 'center',
                     gap: '40px'
                   }}>
                     <div style={{
                       width: '135px',
                       color: 'var(--Color-text-normal, #161616)',
                       fontSize: '16px',
                       fontFamily: 'Pretendard',
                       fontWeight: '600',
                       lineHeight: '24px',
                       wordWrap: 'break-word'
                     }}>
                       총 자리 수
                     </div>
                     <div style={{
                       width: '492px',
                       display: 'flex',
                       padding: '12px 16px',
                       background: 'var(--Color-fill-white, white)',
                       borderRadius: '8px',
                       border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                       justifyContent: 'center',
                       alignItems: 'center'
                     }}>
                       <div style={{
                         flex: '1 1 0',
                         textAlign: 'center',
                         color: 'var(--Color-text-normal, #161616)',
                         fontSize: '13px',
                         fontFamily: 'Pretendard',
                         fontWeight: '500',
                         lineHeight: '19.50px',
                         wordWrap: 'break-word'
                       }}>
                         {reservationRows * reservationCols}
                       </div>
                     </div>
                   </div>

                   {/* 짝 배치 / 교탁 위치 */}
                   <div style={{
                     display: 'flex',
                     justifyContent: 'flex-start',
                     alignItems: 'center',
                     gap: '191px'
                   }}>
                     {/* 짝 배치 */}
                     <div style={{
                       display: 'flex',
                       justifyContent: 'flex-start',
                       alignItems: 'center',
                       gap: '40px'
                     }}>
                       <div style={{
                         width: '135px',
                         color: 'var(--Color-text-normal, #161616)',
                         fontSize: '16px',
                         fontFamily: 'Pretendard',
                         fontWeight: '600',
                         lineHeight: '24px',
                         wordWrap: 'break-word'
                       }}>
                         짝 배치
                       </div>
                       <ToggleSwitch
                         checked={reservationIsPairedSeating}
                         onChange={setReservationIsPairedSeating}
                         size="medium"
                       />
                     </div>

                     {/* 교탁/칠판 위치 */}
                     <div style={{
                       display: 'flex',
                       justifyContent: 'flex-start',
                       alignItems: 'center',
                       gap: '40px'
                     }}>
                       <div style={{
                         width: '132px',
                         color: 'var(--Color-text-normal, #161616)',
                         fontSize: '16px',
                         fontFamily: 'Pretendard',
                         fontWeight: '600',
                         lineHeight: '24px',
                         wordWrap: 'break-word'
                       }}>
                         교탁/칠판 위치
                       </div>
                       <div style={{
                         display: 'flex',
                         gap: '8px'
                       }}>
                         <div
                           style={{
                             padding: '4px 8px',
                             background: reservationBoardPosition === 'top' 
                               ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                               : 'transparent',
                             color: reservationBoardPosition === 'top' 
                               ? 'var(--Color-text-inverse, white)' 
                               : 'var(--Color-text-normal, #161616)',
                             borderRadius: '4px',
                             border: reservationBoardPosition === 'top' 
                               ? 'none' 
                               : '1px solid var(--Color-line-normal, #F0F0F0)',
                             fontSize: '11px',
                             fontFamily: 'Pretendard',
                             fontWeight: '400',
                             lineHeight: '16.50px',
                             cursor: 'pointer',
                             textAlign: 'center'
                           }}
                           onClick={() => setReservationBoardPosition('top')}
                         >
                           상단
                         </div>
                         <div
                           style={{
                             padding: '4px 8px',
                             background: reservationBoardPosition === 'bottom' 
                               ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                               : 'transparent',
                             color: reservationBoardPosition === 'bottom' 
                               ? 'var(--Color-text-inverse, white)' 
                               : 'var(--Color-text-normal, #161616)',
                             borderRadius: '4px',
                             border: reservationBoardPosition === 'bottom' 
                               ? 'none' 
                               : '1px solid var(--Color-line-normal, #F0F0F0)',
                             fontSize: '11px',
                             fontFamily: 'Pretendard',
                             fontWeight: '400',
                             lineHeight: '16.50px',
                             cursor: 'pointer',
                             textAlign: 'center'
                           }}
                           onClick={() => setReservationBoardPosition('bottom')}
                         >
                           하단
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* 아이템 방식 선택 */}
                   <div style={{
                     display: 'flex',
                     justifyContent: 'flex-start',
                     alignItems: 'center',
                     gap: '40px'
                   }}>
                     <div style={{
                       display: 'flex',
                       justifyContent: 'flex-start',
                       alignItems: 'center',
                       gap: '8px'
                     }}>
                       <div style={{
                         width: '108px',
                         color: 'var(--Color-text-normal, #161616)',
                         fontSize: '16px',
                         fontFamily: 'Pretendard',
                         fontWeight: '600',
                         lineHeight: '24px',
                         wordWrap: 'break-word'
                       }}>
                         아이템 방식 선택
                       </div>
                     </div>
                     <div style={{
                       width: '492px'
                     }}>
                       <DropdownButton
                         options={[
                           { value: 'item', label: '아이템 사용 방식' },
                           { value: 'point', label: '포인트 사용 방식' },
                           { value: 'firstCome', label: '선착순 방식' }
                         ]}
                         value={reservationType}
                         onChange={(value) => setReservationType(value as 'item' | 'point' | 'firstCome')}
                         placeholder="아이템 사용 방식 선택"
                         width="100%"
                         height="48px"
                       />
                                           </div>
                    </div>
                  </div>

                  {/* 시작 버튼 */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Button
                      variant="submit"
                      width="193px"
                      onClick={() => {
                        if (!reservationStartDate || !reservationEndDate) {
                          alert('시작 날짜와 종료 날짜를 모두 선택해주세요.');
                          return;
                        }
                        handleStartReservation({
                          startDate: reservationStartDate,
                          endDate: reservationEndDate,
                          rows: reservationRows,
                          cols: reservationCols,
                          isPairedSeating: reservationIsPairedSeating,
                          boardPosition: reservationBoardPosition,
                          reservationType: reservationType
                        });
                      }}
                    >
                      시작
                    </Button>
                  </div>
                 </div>
                 </div>
               </div>
             ) : !isReservationMode && seatingArrangement && Object.keys(seatingArrangement.seats).length === 0 ? (
               <div
                 data-name="seat-management-current-empty"
                 className="seat-management-print-target"
                 ref={seatingPrintRef}
                 style={{
                 width: '100%',
                 height: 'clamp(500px, 70vh, min(88vh, 840px))',
                 padding: '1rem 1.25rem',
                 background: 'var(--Color-background-normal, white)',
                 borderRadius: '1rem',
                 display: 'flex',
                 overflow: 'hidden'
               }}>
                 <div style={{
                   display: 'flex',
                   justifyContent: 'flex-start',
                   alignItems: 'stretch',
                   gap: '1.5rem',
                   width: '100%',
                   height: '100%',
                   minHeight: 0,
                   overflow: 'hidden',
                   boxSizing: 'border-box'
                 }}>
                  <div style={{
                    width: '13.5rem',
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: '1.25rem'
                  }}>
                    <div style={{
                       width: '100%',
                       background: 'var(--Color-background-sub, #F8F8F8)',
                       borderRadius: '0.75rem',
                       display: 'flex',
                       flexDirection: 'column',
                       alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      height: '100%'
                     }}>
                       <div style={{
                         width: '100%',
                         padding: '16px',
                         borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                         display: 'flex',
                         flexDirection: 'column',
                         alignItems: 'flex-start',
                         justifyContent: 'flex-start',
                         gap: '4px'
                       }}>
                         <div style={{
                           width: '100%',
                           color: 'var(--Color-text-normal, #161616)',
                           fontSize: '13px',
                           fontFamily: 'Pretendard',
                           fontWeight: '500',
                           lineHeight: '19.50px',
                           wordWrap: 'break-word'
                         }}>
                           만들 좌석
                         </div>
                         <div style={{
                           width: '100%',
                           display: 'flex',
                           flexDirection: 'row',
                           justifyContent: 'flex-start',
                           alignItems: 'flex-end',
                           gap: '4px'
                         }}>
                           <div style={{
                             height: '38px',
                             color: 'var(--Color-text-normal, #161616)',
                             fontSize: '28px',
                             fontFamily: 'Pretendard',
                             fontWeight: '500',
                             lineHeight: '42px',
                             whiteSpace: 'nowrap',
                             flexShrink: 0
                           }}>
                             {gridRows * gridCols}석
                           </div>
                           <div style={{
                             color: 'var(--Color-text-assistive, #989898)',
                             fontSize: '18px',
                             fontWeight: '500',
                             lineHeight: '27px',
                             whiteSpace: 'nowrap',
                             flexShrink: 0
                           }}>
                             /{gridRows * gridCols}석
                           </div>
                         </div>
                       </div>

                      <div style={{
                        width: '100%',
                        flex: 1,
                        minHeight: 0,
                        padding: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        gap: '12px'
                      }}>
                         <div style={{
                           width: '100%',
                           color: 'var(--Color-text-normal, #161616)',
                           fontSize: '14px',
                           fontFamily: 'Pretendard',
                           fontWeight: '600',
                           lineHeight: '21px',
                           wordWrap: 'break-word'
                         }}>
                           학생 목록
                         </div>
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          gap: '8px',
                          flex: 1,
                          minHeight: 0,
                          overflowY: 'auto'
                        }}>
                           {sortedStudents.map((student) => (
                             <div
                               key={student.uid}
                               style={{
                                 width: '100%',
                                 minHeight: '40px',
                                 padding: '8px 12px',
                                 borderRadius: '8px',
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                                 background: 'var(--Color-fill-hover-2-dg, #EDEDED)'
                               }}
                             >
                               <div style={{
                                 flex: '1 1 0',
                                 display: 'flex',
                                 justifyContent: 'flex-start',
                                 alignItems: 'center',
                                 gap: '4px'
                               }}>
                                 <div style={{
                                   color: 'var(--Color-text-normal, #161616)',
                                   fontSize: '14px',
                                   fontFamily: 'Pretendard',
                                   fontWeight: '500',
                                   lineHeight: '21px',
                                   wordWrap: 'break-word'
                                 }}>
                                   {student.number}번
                                 </div>
                                 <div style={{
                                   flex: '1 1 0',
                                   color: 'var(--Color-text-normal, #161616)',
                                   fontSize: '14px',
                                   fontFamily: 'Pretendard',
                                   fontWeight: '500',
                                   lineHeight: '21px',
                                   wordWrap: 'break-word'
                                 }}>
                                   {student.name}
                                 </div>
                               </div>
                             </div>
                           ))}
                         </div>
                       </div>
                     </div>
                   </div>

                 <div style={{
                   flex: '1',
                   minHeight: 0,
                   overflow: 'auto',
                   display: 'flex',
                   flexDirection: 'column',
                   alignItems: 'center',
                   justifyContent: 'center',
                   gap: '28px',
                   padding: '20px'
                 }}>
                   <div style={{
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     justifyContent: 'center',
                     gap: '8px'
                   }}>
                     <div style={{
                       textAlign: 'center',
                       color: 'var(--Color-text-normal, #161616)',
                       fontSize: '20px',
                       fontFamily: 'Pretendard',
                       fontWeight: '600',
                       lineHeight: '30px',
                       wordWrap: 'break-word'
                     }}>
                       현재 자리 배치 시작하기
                     </div>
                     <div style={{
                       textAlign: 'center',
                       color: 'var(--Color-text-assistive, #989898)',
                       fontSize: '16px',
                       fontFamily: 'Pretendard',
                       fontWeight: '500',
                       lineHeight: '24px',
                       wordWrap: 'break-word'
                     }}>
                       행과 열을 정한 뒤 시작을 눌러 빈 자리 칸을 만듭니다.
                     </div>
                   </div>

                   <div style={{
                     display: 'flex',
                     flexDirection: 'column',
                     gap: '20px',
                     width: '100%',
                     maxWidth: '700px'
                   }}>
                   <div style={{
                     display: 'flex',
                     justifyContent: 'flex-start',
                     alignItems: 'center',
                     gap: '40px'
                   }}>
                     <div style={{
                       width: '135px',
                       color: 'var(--Color-text-normal, #161616)',
                       fontSize: '16px',
                       fontFamily: 'Pretendard',
                       fontWeight: '600',
                       lineHeight: '24px',
                       wordWrap: 'break-word'
                     }}>
                       행 설정
                     </div>
                     <div style={{
                       width: '492px',
                       display: 'flex',
                       padding: '12px 16px',
                       background: 'var(--Color-fill-white, white)',
                       borderRadius: '8px',
                       border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                       justifyContent: 'space-between',
                       alignItems: 'center',
                       gap: '8px'
                     }}>
                       <div
                         style={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           cursor: 'pointer',
                           width: '24px',
                           height: '24px'
                         }}
                         onClick={() => handleGridSizeChange(Math.max(1, gridRows - 1), gridCols)}
                       >
                         <MinusIcon width={13.20} height={1.20} color="#0D0D0D" />
                       </div>
                       <div style={{
                         flex: '1 1 0',
                         textAlign: 'center',
                         color: 'var(--Color-text-normal, #161616)',
                         fontSize: '13px',
                         fontFamily: 'Pretendard',
                         fontWeight: '500',
                         lineHeight: '19.50px',
                         wordWrap: 'break-word'
                       }}>
                         {gridRows}
                       </div>
                       <div
                         style={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           cursor: 'pointer',
                           width: '24px',
                           height: '24px'
                         }}
                         onClick={() => handleGridSizeChange(gridRows + 1, gridCols)}
                       >
                         <PlusIcon width={13.20} height={13.20} fill="#0D0D0D" />
                       </div>
                     </div>
                   </div>

                   <div style={{
                     display: 'flex',
                     justifyContent: 'flex-start',
                     alignItems: 'center',
                     gap: '40px'
                   }}>
                     <div style={{
                       width: '135px',
                       color: 'var(--Color-text-normal, #161616)',
                       fontSize: '16px',
                       fontFamily: 'Pretendard',
                       fontWeight: '600',
                       lineHeight: '24px',
                       wordWrap: 'break-word'
                     }}>
                       열 설정
                     </div>
                     <div style={{
                       width: '492px',
                       display: 'flex',
                       padding: '12px 16px',
                       background: 'var(--Color-fill-white, white)',
                       borderRadius: '8px',
                       border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                       justifyContent: 'space-between',
                       alignItems: 'center',
                       gap: '8px'
                     }}>
                       <div
                         style={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           cursor: 'pointer',
                           width: '24px',
                           height: '24px'
                         }}
                         onClick={() => handleGridSizeChange(gridRows, Math.max(1, gridCols - 1))}
                       >
                         <MinusIcon width={13.20} height={1.20} color="#0D0D0D" />
                       </div>
                       <div style={{
                         flex: '1 1 0',
                         textAlign: 'center',
                         color: 'var(--Color-text-normal, #161616)',
                         fontSize: '13px',
                         fontFamily: 'Pretendard',
                         fontWeight: '500',
                         lineHeight: '19.50px',
                         wordWrap: 'break-word'
                       }}>
                         {gridCols}
                       </div>
                       <div
                         style={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           cursor: 'pointer',
                           width: '24px',
                           height: '24px'
                         }}
                         onClick={() => handleGridSizeChange(gridRows, gridCols + 1)}
                       >
                         <PlusIcon width={13.20} height={13.20} fill="#0D0D0D" />
                       </div>
                     </div>
                   </div>

                   <div style={{
                     display: 'flex',
                     justifyContent: 'flex-start',
                     alignItems: 'center',
                     gap: '40px'
                   }}>
                     <div style={{
                       width: '135px',
                       color: 'var(--Color-text-normal, #161616)',
                       fontSize: '16px',
                       fontFamily: 'Pretendard',
                       fontWeight: '600',
                       lineHeight: '24px',
                       wordWrap: 'break-word'
                     }}>
                       총 자리 수
                     </div>
                     <div style={{
                       width: '492px',
                       display: 'flex',
                       padding: '12px 16px',
                       background: 'var(--Color-fill-white, white)',
                       borderRadius: '8px',
                       border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                       justifyContent: 'center',
                       alignItems: 'center'
                     }}>
                       <div style={{
                         flex: '1 1 0',
                         textAlign: 'center',
                         color: 'var(--Color-text-normal, #161616)',
                         fontSize: '13px',
                         fontFamily: 'Pretendard',
                         fontWeight: '500',
                         lineHeight: '19.50px',
                         wordWrap: 'break-word'
                       }}>
                         {gridRows * gridCols}
                       </div>
                     </div>
                   </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Button variant="submit" width="193px" onClick={handleCreateCurrentSeatsFromEmpty}>
                      시작
                    </Button>
                  </div>
                 </div>
                 </div>
               </div>
             ) : (
            <div
              className="seat-management-print-target"
              ref={seatingPrintRef}
              style={{
               width: '100%',
              height: 'clamp(500px, 70vh, min(88vh, 840px))',
               padding: '1rem 1.25rem',
               background: 'var(--Color-background-normal, white)',
               borderRadius: '1rem',
               display: 'flex',
               overflow: 'hidden'
            }}>
                {/* 왼쪽: 학생 목록 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  gap: '1.5rem',
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  overflow: 'hidden',
                  boxSizing: 'border-box'
                }}>
                  <div style={{
                    width: '13.5rem',
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: '1.25rem'
                  }}>
                    {/* 잔여 자리 카드 (flex로 남는 높이만 사용 — 아래 게시 버튼이 항상 보이도록) */}
                    <div style={{
                      width: '100%',
                      flex: '1 1 0',
                      minHeight: 0,
                      background: 'var(--Color-background-sub, #F8F8F8)',
                      borderRadius: '0.75rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start'
                    }}>
                      <div style={{
                        width: '100%',
                        padding: '16px',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        gap: '4px'
                      }}>
                        <div style={{
                          width: '100%',
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.50px',
                          wordWrap: 'break-word'
                        }}>
                          {isReservationMode ? '예약 현황' : '잔여 자리'}
                        </div>
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-end',
                          gap: '4px'
                        }}>
                          <div style={{
                            height: '38px',
                            color: 'var(--Color-text-normal, #161616)',
                            fontSize: '28px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '42px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}>
                            {isReservationMode ? `${assignedSeatsCount}명` : `${totalSeatsCount - assignedSeatsCount}석`}
                          </div>
                          <div style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '18px',
                            fontWeight: '500',
                            lineHeight: '27px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}>
                            {isReservationMode
                              ? `/ ${totalSeatsCount}석`
                              : `/${totalSeatsCount}석`}
                          </div>
                        </div>
                        {isReservationMode && (
                          <div style={{
                            width: '100%',
                            marginTop: '8px',
                            fontSize: '11px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            color: 'var(--Color-text-assistive, #989898)',
                            lineHeight: '16px'
                          }}>
                            미예약 {Math.max(0, totalSeatsCount - assignedSeatsCount)}석 · 학생이 예약한 자리는 자리판에 이름·「예약」으로 표시됩니다.
                          </div>
                        )}
                      </div>

                      {/* 예약자 명단 (예약 모드) */}
                      {isReservationMode && reservationArrangement && assignedSeatsCount > 0 && (
                        <div style={{
                          width: '100%',
                          padding: '0 16px 12px',
                          borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                        }}>
                          <div style={{
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '600',
                            color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                            marginBottom: '8px'
                          }}>
                            예약자 명단
                          </div>
                          <div style={{
                            maxHeight: '160px',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px'
                          }}>
                            {Object.entries(reservationArrangement.seats)
                              .filter(([, s]) => getSeatOccupantId(s))
                              .sort(([a], [b]) => a.localeCompare(b, 'ko'))
                              .map(([seatKey, s]) => {
                                const [r, c] = seatKey.split('-').map(Number);
                                const label = getSeatPositionLabel(r, c, s);
                                const nm = getLatestStudentName(getSeatOccupantId(s) ?? null, s.studentName ?? s.name ?? null);
                                return (
                                  <div
                                    key={seatKey}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      gap: '8px',
                                      fontSize: '12px',
                                      fontFamily: 'Pretendard',
                                      color: 'var(--Color-text-normal, #161616)',
                                      lineHeight: '18px'
                                    }}
                                  >
                                    <span style={{ color: 'var(--Color-text-assistive, #989898)', flexShrink: 0 }}>{label}</span>
                                    <span style={{ fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{nm ?? '—'}</span>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}

                      {/* 학생 목록 */}
                      <div style={{
                        width: '100%',
                        flex: 1,
                        minHeight: 0,
                        padding: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        gap: '12px'
                      }}>
                        <div style={{
                          width: '100%',
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '600',
                          lineHeight: '21px',
                          wordWrap: 'break-word'
                        }}>
                          학생 목록
                        </div>
                        <Droppable droppableId="studentList" isDropDisabled={currentArrangement?.status === 'published'}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                gap: '8px',
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto'
                              }}
                            >
                              {sortedStudents.map((student, index) => {
                                const isAssigned = Object.values(currentArrangement?.seats || {}).some(
                                  (seat) =>
                                    seat.studentId === student.uid ||
                                    (seat.uid != null && String(seat.uid).trim() === String(student.uid).trim())
                                );

                                return (
                                  <Draggable
                                    key={student.uid}
                                    draggableId={`student-list-${student.uid}`}
                                    index={index}
                                    isDragDisabled={currentArrangement?.status === 'published'}
                                  >
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                          width: '161px',
                                          maxHeight: '40px',
                                          minHeight: '40px',
                                          padding: '8px 12px',
                                          background: isAssigned ? 'var(--Color-fill-hover-2-dg, #EDEDED)' : 'transparent',
                                          borderRadius: '8px',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          cursor: currentArrangement?.status === 'published' ? 'default' : 'grab',
                                          ...provided.draggableProps.style
                                        }}
                                      >
                                        <div style={{
                                          flex: '1 1 0',
                                          display: 'flex',
                                          justifyContent: 'flex-start',
                                          alignItems: 'center',
                                          gap: '4px'
                                        }}>
                                          <div style={{
                                            color: 'var(--Color-text-normal, #161616)',
                                            fontSize: '14px',
                                            fontFamily: 'Pretendard',
                                            fontWeight: '500',
                                            lineHeight: '21px',
                                            wordWrap: 'break-word'
                                          }}>
                                            {student.number}번
                                          </div>
                                          <div style={{
                                            flex: '1 1 0',
                                            color: 'var(--Color-text-normal, #161616)',
                                            fontSize: '14px',
                                            fontFamily: 'Pretendard',
                                            fontWeight: '500',
                                            lineHeight: '21px',
                                            wordWrap: 'break-word'
                                          }}>
                                            {student.name}
                                          </div>
                                        </div>
                                        {isAssigned && (
                                          <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                          }}>
                                            <div style={{
                                              padding: '2px 12px',
                                              background: '#FFD2B5',
                                              borderRadius: '8px',
                                              display: 'flex',
                                              justifyContent: 'center',
                                              alignItems: 'center',
                                              gap: '4px'
                                            }}>
                                              <div style={{
                                                color: 'var(--Color-text-brand-color, #FF6F0F)',
                                                fontSize: '12px',
                                                fontFamily: 'Pretendard',
                                                fontWeight: '500',
                                                lineHeight: '18px',
                                                wordWrap: 'break-word'
                                              }}>
                                                배정됨
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    </div>

                    {/* 게시 상태 (현재 자리 관리에서만) */}
                    {!isReservationMode && (
                      <div
                        style={{
                          width: '100%',
                          flexShrink: 0,
                          padding: '8px 10px',
                          borderRadius: '8px',
                          background:
                            seatingArrangement?.status === 'published'
                              ? 'rgba(255, 111, 15, 0.12)'
                              : 'var(--Color-background-sub, #F8F8F8)',
                          border:
                            seatingArrangement?.status === 'published'
                              ? '1px solid rgba(255, 111, 15, 0.35)'
                              : '1px solid var(--Color-line-normal, #F0F0F0)'
                        }}
                      >
                        <div
                          style={{
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '600',
                            color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                            lineHeight: '18px'
                          }}
                        >
                          {seatingArrangement?.status === 'published' ? (
                            <span style={{ color: 'var(--Color-fill-brand-color, #FF6F0F)' }}>
                              학생 화면에 게시됨
                            </span>
                          ) : (
                            <span style={{ color: 'var(--Color-text-assistive, #989898)' }}>
                              초안 · 아직 학생에게 공개되지 않음
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            marginTop: '4px',
                            fontSize: '11px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            color: 'var(--Color-text-assistive, #989898)',
                            lineHeight: '16px'
                          }}
                        >
                          게시 후에는 자리를 바꾸려면 먼저 게시를 취소하세요.
                        </div>
                      </div>
                    )}

                    {/* 자리 게시/반영 버튼 */}
                    <div style={{
                      width: '100%',
                      flexShrink: 0,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start'
                    }}>
                      {isReservationMode ? (
                        <Button
                          variant="submit"
                          width="100%"
                          leftIcon={<PublishButton width={18} height={18} fill="white" />}
                          onClick={handlePublishArrangement}
                        >
                          자리 반영하기
                        </Button>
                      ) : seatingArrangement?.status === 'published' ? (
                        <Button
                          variant="cancel"
                          width="100%"
                          onClick={handleUnpublishArrangement}
                      >
                          게시 취소
                        </Button>
                      ) : (
                        <Button
                          variant="submit"
                          width="100%"
                          leftIcon={<PublishButton width={18} height={18} fill="white" />}
                          onClick={handlePublishArrangement}
                        >
                          자리 게시
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* 오른쪽: 좌석 배치 영역 */}
                  <div style={{
                    flex: '1',
                    height: '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '16px'
                  }}>
                     {/* 버튼 영역 */}
                     <div style={{
                       width: '100%',
                       flexShrink: 0,
                       display: 'flex',
                       flexDirection: 'column',
                       gap: '12px'
                     }}>

                       {/* 학생 배정 버튼 */}
                       <div style={{
                         width: '100%',
                         display: 'flex',
                         justifyContent: 'flex-start',
                         alignItems: 'center'
                       }}>
                         <div style={{
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'flex-start',
                           justifyContent: 'flex-start',
                           gap: '8px'
                         }}>
                           <div style={{
                             display: 'flex',
                             justifyContent: 'flex-start',
                             alignItems: 'center',
                             gap: '20px',
                             flexWrap: 'wrap'
                           }}>
                             <Button variant="secondary" onClick={handleShuffle}>자리 섞기</Button>
                             <Button variant="secondary" onClick={handleRandomAssign}>랜덤 배치</Button>
                             <Button variant="secondary" onClick={handleAssignByNumber}>번호 순 배치</Button>
                             <Button variant="secondary" onClick={handleResetSeats}>자리 초기화</Button>
                             <Button variant="secondary" onClick={handleAssignUnassignedStudents}>미배정 학생 랜덤 배정</Button>
                             <Button 
                               variant={isDisableMode ? "primary" : "secondary"}
                               onClick={() => {
                                 setIsDisableMode(v => {
                                   const next = !v;
                                   if (next) setIsSeatTaxMode(false);
                                   return next;
                                 });
                               }}
                             >
                               {isDisableMode ? '비활성화 모드 종료' : '비활성화 자리 설정'}
                             </Button>
                             <Button
                               variant={isSeatTaxMode ? "primary" : "secondary"}
                               onClick={() => {
                                 setIsSeatTaxMode(v => {
                                   const next = !v;
                                   if (next) setIsDisableMode(false);
                                   return next;
                                 });
                               }}
                             >
                               {isSeatTaxMode ? '자리세 설정 종료' : '자리세 설정'}
                             </Button>
                           </div>
                         </div>
                       </div>
                       {isSeatTaxMode && (
                         <div style={{
                           width: '100%',
                           padding: '8px 12px',
                           background: '#FFF1E7',
                           borderRadius: '8px',
                           flexShrink: 0
                         }}>
                           <div style={{
                             color: '#FF6F0F',
                             fontSize: '12px',
                             fontFamily: 'Pretendard',
                             fontWeight: '500',
                             textAlign: 'center'
                           }}>
                             자리를 클릭해 자리세(포인트)를 입력합니다. 비워 두고 저장하면 자리세를 해제합니다.
                           </div>
                         </div>
                       )}
                     </div>

                    {/* 편집 모드 안내 */}
                    {isEditLayoutMode && (
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        marginBottom: '8px',
                        flexShrink: 0
                      }}>
                        <div style={{
                          width: '100%',
                          padding: '8px 12px',
                          background: '#FFF1E7',
                          borderRadius: '8px'
                        }}>
                          <div style={{
                            color: '#FF6F0F',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            textAlign: 'center'
                          }}>
                            💡 좌석을 드래그해서 이동 | 빈 칸 더블클릭으로 새 좌석 추가 | 우클릭으로 삭제
                          </div>
                        </div>
                        
                        {/* 그리드 크기 조절 */}
                        <div style={{
                          width: '100%',
                          padding: '8px 12px',
                          background: 'white',
                          border: '1px solid #E9E9E9',
                          borderRadius: '8px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '20px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              color: '#4C4C4C'
                            }}>
                              가로:
                            </span>
                            <button
                              onClick={() => {
                                if (gridCols > 5) {
                                  handleGridSizeChange(gridRows, gridCols - 1);
                                }
                              }}
                              disabled={gridCols <= 5}
                              style={{
                                width: '24px',
                                height: '24px',
                                border: 'none',
                                background: gridCols <= 5 ? '#F0F0F0' : '#FF6F0F',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: gridCols <= 5 ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              -
                            </button>
                            <span style={{
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: '600',
                              color: '#161616',
                              minWidth: '30px',
                              textAlign: 'center'
                            }}>
                              {gridCols}
                            </span>
                            <button
                              onClick={() => {
                                if (gridCols < 20) {
                                  handleGridSizeChange(gridRows, gridCols + 1);
                                }
                              }}
                              disabled={gridCols >= 20}
                              style={{
                                width: '24px',
                                height: '24px',
                                border: 'none',
                                background: gridCols >= 20 ? '#F0F0F0' : '#FF6F0F',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: gridCols >= 20 ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              +
                            </button>
                          </div>
                          
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              color: '#4C4C4C'
                            }}>
                              세로:
                            </span>
                            <button
                              onClick={() => {
                                if (gridRows > 4) {
                                  handleGridSizeChange(gridRows - 1, gridCols);
                                }
                              }}
                              disabled={gridRows <= 4}
                              style={{
                                width: '24px',
                                height: '24px',
                                border: 'none',
                                background: gridRows <= 4 ? '#F0F0F0' : '#FF6F0F',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: gridRows <= 4 ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              -
                            </button>
                            <span style={{
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: '600',
                              color: '#161616',
                              minWidth: '30px',
                              textAlign: 'center'
                            }}>
                              {gridRows}
                            </span>
                            <button
                              onClick={() => {
                                if (gridRows < 15) {
                                  handleGridSizeChange(gridRows + 1, gridCols);
                                }
                              }}
                              disabled={gridRows >= 15}
                              style={{
                                width: '24px',
                                height: '24px',
                                border: 'none',
                                background: gridRows >= 15 ? '#F0F0F0' : '#FF6F0F',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: gridRows >= 15 ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div
                      ref={seatBoardFitAreaRef}
                      data-name="seat-board-fit-area"
                      style={{
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                      }}
                    >
                      <div
                        ref={seatBoardContentRef}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          padding: isEditLayoutMode ? '12px 16px' : '8px 12px',
                          boxSizing: 'border-box',
                          gap: '8px'
                        }}
                      >
                        {/* 칠판 */}
                        <div
                          className="seat-management-board-section"
                          style={{
                            width: '100%',
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            marginBottom: '4px'
                          }}
                        >
                          <div
                            className="seat-management-board-label"
                            style={{
                              width: '100%',
                              textAlign: 'center',
                              color: 'var(--Color-text-minor, #BABABA)',
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: '600',
                              lineHeight: '21px',
                              wordWrap: 'break-word'
                            }}
                          >
                            칠판
                          </div>
                          <div
                            className="seat-management-board-icon"
                            style={{
                              width: '100%',
                              maxWidth: `${teacherSeatSizes.px(500)}px`,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                          >
                            <Intersect
                              width={teacherSeatSizes.px(500)}
                              height={teacherSeatSizes.px(38)}
                            />
                          </div>
                        </div>

                        <div
                          className="seat-management-grid-section"
                          style={{ flexShrink: 0, width: 'max-content' }}
                        >
                          {renderGrid()}
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
            )}

            {/* 새 자리 배치하기 버튼 (오른쪽 아래) */}
            {((!isReservationMode && seatingArrangement?.status !== 'published') || (isReservationMode && reservationArrangement)) && (
              <div
                data-name="seat-management-add-arrangement-button"
                className="seat-management-floating-button seat-management-add-button"
                onClick={() => setIsAddNewSeatDialogOpen(true)}
                style={{
                  position: 'fixed',
                  right: '52px',
                  bottom: '60px',
                  width: '48px',
                  height: '48px',
                  padding: '9px',
                  background: 'white',
                  boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)',
                  borderRadius: '24px',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  zIndex: 1000
                }}
                title="새 자리 배치하기"
              >
                <PlusIcon width={16.5} height={16.5} fill="#FF6F0F" />
              </div>
            )}

            {/* 편집 모드 토글 버튼 (위치 편집) */}
            {((!isReservationMode && seatingArrangement?.status !== 'published') || (isReservationMode && reservationArrangement)) && (
              <div
                data-name="seat-management-layout-edit-toggle"
                className="seat-management-floating-button seat-management-edit-toggle-button"
                onClick={() => {
                  if (!isEditLayoutMode) setIsSeatTaxMode(false);
                  setIsEditLayoutMode(!isEditLayoutMode);
                }}
                style={{
                  position: 'fixed',
                  right: '52px',
                  bottom: '120px',
                  width: '48px',
                  height: '48px',
                  padding: '9px',
                  background: 'white',
                  boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)',
                  borderRadius: '24px',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  zIndex: 1000
                }}
                title={isEditLayoutMode ? '편집 모드 종료' : '좌석 위치 편집'}
              >
                {isEditLayoutMode ? (
                  <div style={{ color: '#FF6F0F', fontSize: '24px', fontWeight: 'bold' }}>✓</div>
                ) : (
                  <div style={{ color: '#FF6F0F', fontSize: '20px' }}>✎</div>
                )}
              </div>
            )}

            {/* 자리 배치 설정 편집 버튼 */}
            {((!isReservationMode && currentArrangement && seatingArrangement?.status !== 'published') || (isReservationMode && reservationArrangement)) && (
              <div
                data-name="seat-management-settings-button"
                className="seat-management-floating-button seat-management-settings-button"
                onClick={() => setIsEditSeatSettingsDialogOpen(true)}
                style={{
                  position: 'fixed',
                  right: '52px',
                  bottom: '180px',
                  width: '48px',
                  height: '48px',
                  padding: '9px',
                  background: 'white',
                  boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)',
                  borderRadius: '24px',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  zIndex: 1000
                }}
                title="자리 배치 설정"
              >
                <SettingIcon width={16.5} height={16.5} color="#FF6F0F" />
            </div>
            )}

            {/* 예약 자리표 삭제 버튼 */}
            {isReservationMode && reservationArrangement && (
              <div
                data-name="seat-management-delete-reservation-button"
                className="seat-management-floating-button seat-management-delete-button"
                onClick={handleDeleteReservationArrangement}
                style={{
                  position: 'fixed',
                  right: '52px',
                  bottom: '240px',
                  width: '48px',
                  height: '48px',
                  padding: '9px',
                  background: 'white',
                  boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)',
                  borderRadius: '24px',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  zIndex: 1000
                }}
                title="예약 자리표 삭제"
              >
                <TrashIcon width={18} height={18} color="#FF6F0F" />
            </div>
            )}

            {/* 현재 자리표 삭제(좌석 비움 → 시작 화면) — 게시 전 초안만 */}
            {!isReservationMode && seatingArrangement && seatingArrangement.status !== 'published' && (
              <div
                data-name="seat-management-delete-current-button"
                className="seat-management-floating-button seat-management-delete-current-button"
                onClick={handleResetCurrentSeatingArrangement}
                style={{
                  position: 'fixed',
                  right: '52px',
                  bottom: '240px',
                  width: '48px',
                  height: '48px',
                  padding: '9px',
                  background: 'white',
                  boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)',
                  borderRadius: '24px',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  zIndex: 1000
                }}
                title="현재 자리표 삭제 (자리 칸을 비우고 시작 화면으로)"
              >
                <TrashIcon width={18} height={18} color="#FF6F0F" />
              </div>
            )}
        </div>

        {seatTaxTargetKey && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="seat-tax-dialog-title"
            onClick={() => closeSeatTaxDialog()}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1100,
              fontFamily: 'var(--font-family-primary, Pretendard)'
            }}
          >
            <div onClick={e => e.stopPropagation()}>
              <Card
                width="400px"
                padding="0"
                borderRadius="16px"
                background="var(--Color-background-normal, white)"
                boxShadow="0px 8px 16px rgba(0, 0, 0, 0.12)"
              >
                <div style={{
                  margin: '24px 28px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--gap-level-4)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div
                        id="seat-tax-dialog-title"
                        style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '18px',
                          fontWeight: 600,
                          lineHeight: '27px'
                        }}
                      >
                        자리세 설정
                      </div>
                      <div style={{
                        marginTop: '4px',
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: '21px'
                      }}>
                        이 자리에 부과할 포인트(세)를 입력하세요.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={closeSeatTaxDialog}
                      aria-label="닫기"
                      style={{
                        padding: '4px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CloseIcon width={20} height={20} />
                    </button>
                  </div>
                  <InputField
                    value={seatTaxDraft}
                    onChange={setSeatTaxDraft}
                    label="자리세 (정수)"
                    placeholder="예: 100 (비우면 해제)"
                    size="medium"
                  />
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button variant="secondary" onClick={closeSeatTaxDialog}>
                      취소
                    </Button>
                    <Button variant="primary" onClick={handleSaveSeatTax}>
                      저장
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* 새 자리 배치 다이얼로그 */}
        <AddNewSeatDialog
          isOpen={isAddNewSeatDialogOpen}
          onClose={() => setIsAddNewSeatDialogOpen(false)}
          onSave={handleCreateNewArrangement}
          mode="create"
        />

        {/* 자리 배치 설정 편집 다이얼로그 */}
        {currentArrangement && (
          <AddNewSeatDialog
            isOpen={isEditSeatSettingsDialogOpen}
            onClose={() => setIsEditSeatSettingsDialogOpen(false)}
            onSave={handleUpdateSeatSettings}
            mode="edit"
            initialData={{
              startDate: currentArrangement.settings.startDate 
                ? format(currentArrangement.settings.startDate, 'yyyy-MM-dd')
                : '',
              endDate: currentArrangement.settings.endDate
                ? format(currentArrangement.settings.endDate, 'yyyy-MM-dd')
                : '',
              rows: currentArrangement.settings.rows,
              cols: currentArrangement.settings.cols,
              isPairedSeating: currentArrangement.settings.isPairedSeating,
              boardPosition: currentArrangement.settings.boardPosition
            }}
          />
        )}



        {/* TODO: Dialog 컴포넌트들을 별도 파일로 분리 예정 
            - 게시 기간 설정 다이얼로그
            - 좌석 옵션 다이얼로그
            - 예약 모드 선택 다이얼로그
            - 예약 좌석배치도 다이얼로그
            - 컨텍스트 메뉴
        */}
        </div>
        </div>
        </div>
      </DragDropContext>
  );
};

/** 라우트에서 사용하는 페이지: useAuth로 교사·학급 확인 후 SeatManagement 렌더 */
const SeatManagementPage: React.FC = () => {
  const { currentUser } = useAuth();
  if (!currentUser || !isTeacher(currentUser)) {
    return <Navigate to="/" replace />;
  }
  const { schoolId, homeroomClass } = currentUser;
  if (!schoolId || !homeroomClass?.grade || !homeroomClass?.class) {
    return <Navigate to="/" replace />;
  }
  const classId = `${schoolId}_${homeroomClass.grade}_${homeroomClass.class}`;
  return <SeatManagement schoolId={schoolId} classId={classId} />;
};

export default SeatManagementPage;

